import "@fontsource/sarabun/400.css";
import "@fontsource/sarabun/600.css";
import "@fontsource/sarabun/700.css";
import "@fontsource/sarabun/800.css";
import { calculateContractPlan, calculatePayoff, calculatePrepaymentPlan } from "./finance.js";
import "./styles.css?v=golden-landpay-35";
import "./mobile.css?v=golden-landpay-35";

const demoContracts = [
  { id: 1, workspaceId: 1, customer: "สมชาย ใจดี", initials: "สช", phone: "089 428 1650", plot: "A-12", contractNo: "LD-2569-012", totalPrice: 620000, downPayment: 120000, installmentAmount: 10000, totalInstallments: 50, paidInstallments: 15, paidTotal: 150000, dueDay: 5, monthPaid: 10000, status: "paid", tone: "emerald" },
  { id: 2, workspaceId: 1, customer: "วรรณา ทองสุข", initials: "วท", phone: "081 702 3491", plot: "B-07", contractNo: "LD-2569-007", totalPrice: 480000, downPayment: 80000, installmentAmount: 8000, totalInstallments: 50, paidInstallments: 12, paidTotal: 96000, dueDay: 8, monthPaid: 4000, status: "partial", tone: "amber" },
  { id: 3, workspaceId: 1, customer: "ธนา พรหมมา", initials: "ธพ", phone: "095 661 2088", plot: "C-03", contractNo: "LD-2569-003", totalPrice: 750000, downPayment: 150000, installmentAmount: 12000, totalInstallments: 50, paidInstallments: 9, paidTotal: 108000, dueDay: 3, monthPaid: 0, status: "overdue", tone: "ruby" },
  { id: 4, workspaceId: 2, customer: "พิมพ์ชนก แสงดี", initials: "พส", phone: "086 915 7732", plot: "A-18", contractNo: "LD-2569-018", totalPrice: 560000, downPayment: 60000, installmentAmount: 10000, totalInstallments: 50, paidInstallments: 18, paidTotal: 180000, dueDay: 12, monthPaid: 0, status: "upcoming", tone: "sapphire" },
  { id: 5, workspaceId: 2, customer: "มานพ ศรีสุข", initials: "มศ", phone: "092 550 4812", plot: "D-09", contractNo: "LD-2569-009", totalPrice: 430000, downPayment: 70000, installmentAmount: 9000, totalInstallments: 40, paidInstallments: 21, paidTotal: 189000, dueDay: 15, monthPaid: 0, status: "upcoming", tone: "emerald" },
  { id: 6, workspaceId: 2, customer: "สุรีย์พร จันทร์งาม", initials: "สจ", phone: "083 144 6209", plot: "B-14", contractNo: "LD-2569-014", totalPrice: 680000, downPayment: 80000, installmentAmount: 12000, totalInstallments: 50, paidInstallments: 7, paidTotal: 84000, dueDay: 20, monthPaid: 0, status: "upcoming", tone: "amber" },
];

const demoWorkspaces = [
  { id: 1, slug: "chokchai-choomuang", name: "Chokchai Choomuang", initials: "CC" },
  { id: 2, slug: "charinthip-senaphan", name: "Charinthip Senaphan", initials: "CS" },
];

const DEVICE_TOKEN_KEY = "golden-landpay-device-token-v2";
const WORKSPACE_KEY = "golden-landpay-workspace-id";

const statusLabels = {
  paid: "ชำระแล้ว",
  partial: "ชำระบางส่วน",
  due: "ครบกำหนดวันนี้",
  overdue: "ค้างชำระ",
  upcoming: "รอชำระ",
  waived: "อนุญาตข้ามงวด",
};

const thaiMonthNames = ["มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน", "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"];

const demoPreviewScreen = isDemoHost() ? new URLSearchParams(window.location.search).get("preview") : "";
const isDashboardPreview = ["dashboard", "customers", "contract", "payment", "workspace", "offer"].includes(demoPreviewScreen);
const storedWorkspaceId = Number(localStorage.getItem(WORKSPACE_KEY)) || null;
const initialWorkspaceId = demoPreviewScreen === "workspace" ? null : isDashboardPreview ? 1 : storedWorkspaceId;

function detectDeviceLayout() {
  const userAgent = navigator.userAgent || "";
  const isIPad = /iPad/i.test(userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isPhoneUserAgent = /iPhone|iPod|Android.*Mobile|Windows Phone|IEMobile|Opera Mini/i.test(userAgent);
  const phoneSizedScreen = Math.min(window.innerWidth, window.innerHeight) < 700;
  return !isIPad && (isPhoneUserAgent || phoneSizedScreen) ? "mobile" : "ipad";
}

let deviceLayout = detectDeviceLayout();
document.documentElement.dataset.device = deviceLayout;

const state = {
  authenticated: Boolean(localStorage.getItem(DEVICE_TOKEN_KEY) || isDashboardPreview),
  screen: demoPreviewScreen === "payment" ? "contract" : ["customers", "contract", "offer"].includes(demoPreviewScreen) ? demoPreviewScreen : "dashboard",
  workspaces: structuredClone(demoWorkspaces),
  activeWorkspaceId: initialWorkspaceId,
  contracts: isDemoHost() && initialWorkspaceId ? structuredClone(demoContracts.filter((item) => item.workspaceId === initialWorkspaceId)) : [],
  monthlyIncome: initialWorkspaceId === 2 ? [21000, 28000, 25000, 34000, 31000, 0] : [18000, 24500, 22000, 31000, 27000, 14000],
  monthlyInterest: [0, 0, 0, 0, 0, 0],
  interestStats: { receivedThisMonth: 0, principalThisMonth: 0, total: 0, receivedLifetime: 0, outstanding: 0 },
  selectedId: demoPreviewScreen === "payment" ? 3 : 1,
  installments: {},
  prepayments: {},
  search: "",
  filter: "all",
  installmentFilter: "all",
  installmentPage: 1,
  offerDraft: { totalPrice: "", downPayment: "", installmentAmount: "", interestMode: "none", interestRate: "", interestAmount: "" },
  installPrompt: null,
  showInstall: !window.matchMedia("(display-mode: standalone)").matches,
};

const app = document.querySelector("#app");
const overlayRoot = document.querySelector("#overlay-root");
const toast = document.querySelector("#toast");

const baht = (value) => new Intl.NumberFormat("th-TH", { maximumFractionDigits: 0 }).format(Number(value || 0));
const clamp = (value, min = 0, max = 100) => Math.min(max, Math.max(min, Number(value) || 0));
const monthName = () => new Intl.DateTimeFormat("th-TH", { month: "long", year: "numeric" }).format(new Date());
const shortDate = (date) => new Intl.DateTimeFormat("th-TH", { day: "numeric", month: "short", year: "2-digit" }).format(date);
const fullDate = (date) => new Intl.DateTimeFormat("th-TH", { day: "numeric", month: "long", year: "numeric" }).format(date);
const dateInputValue = (date = new Date()) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const escapeHtml = (value) => String(value ?? "").replace(/[&<>'"]/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" })[character]);

function thaiStartMonthFields(date = new Date()) {
  const buddhistYear = date.getFullYear() + 543;
  const months = thaiMonthNames.map((name, index) => `<option value="${index + 1}" ${index === date.getMonth() ? "selected" : ""}>${name}</option>`).join("");
  const years = Array.from({ length: 17 }, (_, index) => buddhistYear + 1 - index).map((year) => `<option value="${year}" ${year === buddhistYear ? "selected" : ""}>พ.ศ. ${year}</option>`).join("");
  return `<div class="thai-start-month" data-start-month><select name="start_month" aria-label="เดือนเริ่มผ่อน" required>${months}</select><select name="start_year_be" aria-label="ปี พ.ศ. ที่เริ่มผ่อน" required>${years}</select><input name="start_date" type="hidden" value="${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-01"></div>`;
}

function syncThaiStartMonth(container) {
  const monthSelect = container.querySelector('[name="start_month"]');
  const yearSelect = container.querySelector('[name="start_year_be"]');
  const hiddenInput = container.querySelector('[name="start_date"]');
  if (!monthSelect || !yearSelect || !hiddenInput) return;
  const christianYear = Number(yearSelect.value) - 543;
  hiddenInput.value = `${christianYear}-${String(Number(monthSelect.value)).padStart(2, "0")}-01`;
}

function isDemoHost() {
  return ["localhost", "127.0.0.1"].includes(window.location.hostname);
}

function metrics() {
  const expected = state.contracts.reduce((sum, item) => sum + Number(item.expectedAmount ?? (item.status === "waived" ? 0 : item.installmentAmount)), 0);
  const installmentMonthPaid = state.contracts.reduce((sum, item) => sum + Number(item.monthPaid), 0);
  const received = state.monthlyIncome?.length ? Number(state.monthlyIncome.at(-1) || 0) : installmentMonthPaid;
  const financed = state.contracts.reduce((sum, item) => sum + Math.max(0, Number(item.totalPrice) - Number(item.downPayment)), 0);
  const paid = state.contracts.reduce((sum, item) => sum + Number(item.paidPrincipal ?? item.paidTotal), 0);
  const remaining = state.contracts.reduce((sum, item) => sum + Math.max(0, item.totalPrice - item.downPayment - Number(item.paidPrincipal ?? item.paidTotal)), 0);
  return { expected, received, pending: Math.max(0, expected - installmentMonthPaid), remaining, financed, paid };
}

function selectedContract() {
  return state.contracts.find((item) => item.id === state.selectedId) || state.contracts[0];
}

function activeWorkspace() {
  return state.workspaces.find((item) => Number(item.id) === Number(state.activeWorkspaceId)) || null;
}

function installmentUnlockDate(dueDate) {
  const date = new Date(dueDate);
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function isInstallmentLocked(dueDate) {
  if (!dueDate) return true;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today < installmentUnlockDate(dueDate);
}

function isHistoricalPayableInstallment(installment) {
  if (!installment || ["paid", "waived"].includes(installment.status)) return false;
  const dueDate = new Date(installment.dueDate);
  dueDate.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return dueDate < today;
}

function firstOpenInstallment(installments = []) {
  return installments.find((item) => !["paid", "waived"].includes(item.status) && Number(item.paid) < Number(item.amount)) || null;
}

function installmentForPaymentDate(installments = [], paidDateValue = dateInputValue()) {
  if (!installments.length) return null;
  const paidDate = new Date(`${String(paidDateValue || dateInputValue())}T00:00:00`);
  if (Number.isNaN(paidDate.getTime())) return installments[0] || null;
  const sameMonth = installments.find((item) => {
    const dueDate = new Date(item.dueDate);
    return dueDate.getFullYear() === paidDate.getFullYear() && dueDate.getMonth() === paidDate.getMonth();
  });
  if (sameMonth) return sameMonth;
  return [...installments].reverse().find((item) => new Date(item.dueDate) <= paidDate) || installments[0] || null;
}

function isActionableInstallment(installment, installments = []) {
  const firstOpen = firstOpenInstallment(installments);
  return Boolean(installment && firstOpen && Number(firstOpen.no) === Number(installment.no));
}

function buildInstallments(contract) {
  if (!contract) return [];
  const now = new Date();
  const rows = [];
  const plan = calculateContractPlan({ totalPrice: contract.totalPrice, downPayment: contract.downPayment, installmentAmount: contract.installmentAmount, interestMode: contract.interestMode, interestRate: contract.interestRate, interestAmount: contract.interestAmount });
  const firstVisible = Math.max(1, contract.paidInstallments - 2);
  for (let number = firstVisible; number <= Math.min(contract.totalInstallments, firstVisible + 8); number += 1) {
    const monthOffset = number - contract.paidInstallments - 1;
    const startDate = contract.startDate ? new Date(`${contract.startDate}T00:00:00`) : null;
    const dueDate = startDate && !Number.isNaN(startDate.getTime())
      ? new Date(startDate.getFullYear(), startDate.getMonth() + number - 1, contract.dueDay)
      : new Date(now.getFullYear(), now.getMonth() + monthOffset, contract.dueDay);
    let status = "upcoming";
    let paid = 0;
    const remainingBefore = Math.max(0, plan.financedPrincipal - plan.monthlyPrincipal * (number - 1));
    const principal = Math.min(plan.monthlyPrincipal, remainingBefore);
    const interest = plan.monthlyInterest;
    const amount = principal + interest;
    if (number <= contract.paidInstallments) {
      status = "paid";
      paid = amount;
    } else if (number === contract.paidInstallments + 1) {
      status = contract.status;
      paid = contract.monthPaid;
    }
    rows.push({ no: number, dueDate, amount, paid, principal, interest, principalPaid: status === "paid" ? principal : 0, interestPaid: status === "paid" ? interest : 0, status, slip: status === "paid" ? `SLIP-${contract.contractNo}-${number}` : "" });
  }
  return rows;
}

function render() {
  if (!state.authenticated) {
    app.innerHTML = loginView();
    return;
  }
  if (!state.activeWorkspaceId) {
    app.innerHTML = workspacePickerView();
    return;
  }
  if (state.screen === "customers") app.innerHTML = appFrame(customersView(), "customers");
  else if (state.screen === "contract") app.innerHTML = appFrame(contractView(), "contract");
  else if (state.screen === "offer") app.innerHTML = appFrame(offerView(), "offer");
  else app.innerHTML = appFrame(deviceLayout === "mobile" ? mobileDashboardView() : dashboardView(), "dashboard");
}

function appFrame(content, active) {
  const showNav = active !== "contract";
  return `<main class="app-shell"><div class="app-surface">${content}${showNav ? bottomNav(active) : ""}</div></main>`;
}

function loginView() {
  return `
    <main class="login-screen">
      <section class="login-card">
        <div class="login-visual" aria-hidden="true">
          <div class="brand-seal"><span>GL</span><small>EST. 2026</small></div>
          <div class="gold-orbit orbit-one"></div><div class="gold-orbit orbit-two"></div>
          <div class="visual-bars"><i></i><i></i><i></i><i></i><i></i></div>
          <div class="visual-ring"><div><strong>75%</strong><small>ชำระแล้ว</small></div></div>
        </div>
        <div class="login-content">
          <p class="overline">ระบบจัดการผ่อนที่ดิน</p>
          <h1>GOLDEN<br>LANDPAY</h1>
          <p class="lead">ทุกสัญญา ทุกงวด และทุกยอดรับชำระ ดูแลได้อย่างเป็นระบบในที่เดียว</p>
          <form id="login-form" class="form-stack">
            <label>รหัสผ่านสำหรับเข้าใช้งานครั้งแรก
              <div class="password-control"><input name="password" type="password" inputmode="numeric" minlength="5" maxlength="5" pattern="[0-9]{5}" required placeholder="รหัสผ่าน 5 หลัก" autocomplete="new-password"><button type="button" data-action="toggle-password">แสดง</button></div>
            </label>
            <button class="button button-gold" type="submit">เข้าสู่ระบบ <span>→</span></button>
          </form>
          <p class="fine-print">ยืนยันเพียงครั้งแรก เครื่องนี้จะจดจำการเข้าใช้งานและพื้นที่ทำงานล่าสุด</p>
        </div>
      </section>
    </main>`;
}

function workspacePickerView() {
  return `
    <main class="workspace-screen">
      <section class="workspace-shell">
        <header class="workspace-brand"><span class="workspace-logo">GL</span><div><small>GOLDEN</small><strong>LANDPAY</strong></div></header>
        <div class="workspace-intro"><p class="overline">เลือกพื้นที่ทำงาน</p><h1>วันนี้คุณจะจัดการ<br>ข้อมูลของใคร?</h1><p>ข้อมูลลูกค้า ที่ดิน สัญญา งวด และสลิปจะแยกจากกันอย่างสมบูรณ์</p></div>
        <div class="workspace-options">
          ${state.workspaces.map((workspace, index) => `<button class="workspace-option ${index === 0 ? "gold" : "cyan"}" data-action="select-workspace" data-workspace-id="${workspace.id}">
            <span class="workspace-avatar">${escapeHtml(workspace.initials || workspace.name.split(" ").map((part) => part[0]).join(""))}</span>
            <span class="workspace-name"><small>WORKSPACE ${String(index + 1).padStart(2, "0")}</small><strong>${escapeHtml(workspace.name)}</strong><em>แตะเพื่อเลือกและจดจำพื้นที่นี้</em></span>
            <b>→</b>
          </button>`).join("")}
        </div>
        <div class="workspace-security"><span>✓</span><p><strong>แยกข้อมูลอย่างปลอดภัย</strong><small>ระบบจะแสดงเฉพาะข้อมูลของพื้นที่ที่เลือกเท่านั้น</small></p></div>
      </section>
    </main>`;
}

function dashboardView() {
  const summary = metrics();
  const interest = state.interestStats || {};
  const percent = summary.expected ? Math.round((summary.received / summary.expected) * 100) : 0;
  const portfolioPercent = summary.financed ? Math.round((summary.paid / summary.financed) * 100) : 0;
  const allDownPayments = state.contracts.reduce((sum, item) => sum + Number(item.downPayment || 0), 0);
  const allPrincipalReceived = state.contracts.reduce((sum, item) => sum + Number(item.paidPrincipal ?? item.paidTotal ?? 0), 0);
  const allInterestReceived = state.contracts.reduce((sum, item) => sum + Number(item.paidInterest || 0), 0);
  const allCashReceived = allDownPayments + allPrincipalReceived + allInterestReceived;
  const allContractCash = state.contracts.reduce((sum, item) => sum + Number(item.totalPrice || 0) + Number(item.totalInterest || 0), 0);
  const allCashPercent = allContractCash ? Math.round((allCashReceived / allContractCash) * 100) : 0;
  const monthInterestPercent = summary.received ? Math.round((Number(interest.receivedThisMonth || 0) / summary.received) * 100) : 0;
  const outstandingInterestPercent = Number(interest.total) ? Math.round((Number(interest.outstanding || 0) / Number(interest.total)) * 100) : 0;
  const urgent = state.contracts.filter((item) => ["overdue", "partial", "due"].includes(item.status));
  const overdueCount = state.contracts.filter((item) => item.status === "overdue").length;
  const today = new Date();
  const dueToday = state.contracts.filter((item) => item.status !== "waived" && Number(item.dueDay) === today.getDate());
  const monthlyIncome = [...(state.monthlyIncome || []).slice(-6)];
  while (monthlyIncome.length < 6) monthlyIncome.unshift(0);
  const monthlyLabels = Array.from({ length: 6 }, (_, index) => new Intl.DateTimeFormat("th-TH", { month: "short" }).format(new Date(today.getFullYear(), today.getMonth() - 5 + index, 1)));
  const monthlyPeak = Math.max(...monthlyIncome, 1);
  const monthlyAverage = monthlyIncome.reduce((sum, amount) => sum + amount, 0) / monthlyIncome.length;
  const monthlyHeights = monthlyIncome.map((amount) => Math.max(16, Math.round((amount / monthlyPeak) * 100)));
  const linePoints = monthlyHeights.map((height, index) => `${50 + index * 100},${148 - height * 1.08}`).join(" ");
  const compactMoney = (amount) => amount >= 1000 ? `${Math.round(amount / 1000)}k` : String(amount);
  return `
    <section class="screen dashboard-screen trading-dashboard">
      <header class="trade-header">
        <div class="trade-brand"><button data-action="customers" aria-label="เปิดรายการลูกค้า">☰</button><span class="trade-logo">GL</span><div><strong>GOLDEN LANDPAY</strong><small>LAND INSTALLMENT ANALYTICS</small></div></div>
        <div class="trade-session"><span><i></i> LIVE DATA</span><small>${fullDate(today)}</small></div>
        <div class="trade-actions"><button class="trade-workspace" data-action="switch-workspace"><small>WORKSPACE</small><strong>${escapeHtml(activeWorkspace()?.name || "")}</strong><b>⌄</b></button><button class="trade-settings" data-action="settings" aria-label="ตั้งค่า">•••</button></div>
      </header>

      <section class="trade-kpi-strip" aria-label="สรุปตัวเลขสำคัญ">
        <article><small>ACTIVE CONTRACTS</small><strong>${state.contracts.length}</strong><em>แปลงกำลังผ่อน</em></article>
        <article class="primary"><small>RECEIVED THIS MONTH</small><strong>฿${baht(summary.received)}</strong><em>${percent}% ของยอดที่ควรรับ</em></article>
        <article><small>EXPECTED</small><strong>฿${baht(summary.expected)}</strong><em>ยอดรับเดือนนี้</em></article>
        <article><small>PENDING</small><strong>฿${baht(summary.pending)}</strong><em>ยังไม่ได้รับ</em></article>
        <article class="risk"><small>OVERDUE</small><strong>${overdueCount}</strong><em>รายการค้างชำระ</em></article>
      </section>

      <div class="trade-main-grid">
        <section class="trade-chart-panel">
          <div class="trade-panel-head"><div><p>CASH FLOW / 6M</p><h2>แนวโน้มเงินรับรายเดือน</h2></div><div class="chart-period"><button class="active">6M</button><button>YTD</button><button>1Y</button></div></div>
          <div class="trade-chart-summary"><strong>฿${baht(summary.received)}</strong><span class="${summary.received >= monthlyAverage ? "positive" : "negative"}">${summary.received >= monthlyAverage ? "+" : "−"}${Math.abs(Math.round(((summary.received - monthlyAverage) / Math.max(1, monthlyAverage)) * 100))}% vs AVG</span></div>
          <div class="trade-line-stage">
            <div class="trade-y-axis"><span>฿${compactMoney(monthlyPeak)}</span><span>฿${compactMoney(monthlyPeak / 2)}</span><span>฿0</span></div>
            <svg class="trade-line-svg" viewBox="0 0 600 160" preserveAspectRatio="none" aria-label="กราฟเส้นเงินรับย้อนหลัง 6 เดือน">
              <defs><linearGradient id="trade-line-gradient" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="#279dc4"/><stop offset=".72" stop-color="#73d8ee"/><stop offset="1" stop-color="#f2c94c"/></linearGradient><linearGradient id="trade-area-gradient" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#46c8e8" stop-opacity=".22"/><stop offset=".62" stop-color="#1b8cad" stop-opacity=".07"/><stop offset="1" stop-color="#f2c94c" stop-opacity="0"/></linearGradient></defs>
              <polygon class="trade-area" points="${linePoints} 550,158 50,158"/>
              <polyline class="trade-line" points="${linePoints}"/>
              ${monthlyHeights.map((height, index) => `<circle class="trade-point ${index === 5 ? "current" : ""}" cx="${50 + index * 100}" cy="${148 - height * 1.08}" r="${index === 5 ? 7 : 5}"/>`).join("")}
            </svg>
            <div class="trade-value-labels">${monthlyIncome.map((amount) => `<span>฿${compactMoney(amount)}</span>`).join("")}</div>
          </div>
          <div class="trade-month-labels">${monthlyLabels.map((label) => `<span>${label}</span>`).join("")}</div>
          <footer><span><small>AVG 6M</small><strong>฿${baht(monthlyAverage)}</strong></span><span><small>HIGH</small><strong>฿${baht(monthlyPeak)}</strong></span><span><small>LOW</small><strong>฿${baht(Math.min(...monthlyIncome))}</strong></span><span><small>LATEST</small><strong>฿${baht(summary.received)}</strong></span></footer>
        </section>

        <aside class="trade-portfolio-panel">
          <div class="trade-panel-head"><div><p>PORTFOLIO STATUS</p><h2>ภาพรวมสัญญา</h2></div><span class="health-dot">STABLE</span></div>
          <div class="trade-donut-row"><div class="trade-donut" style="--progress:${clamp(percent) * 3.6}deg"><div><strong>${state.contracts.length}</strong><small>แปลง</small></div></div><div class="trade-received"><small>รับแล้วเดือนนี้</small><strong>฿${baht(summary.received)}</strong><span>จากเป้าหมาย ฿${baht(summary.expected)}</span></div></div>
          <div class="trade-grand-received-card">
            <div class="trade-grand-received-donut" style="--progress:${clamp(allCashPercent) * 3.6}deg"><div><strong>${allCashPercent}%</strong><small>รับสะสม</small></div></div>
            <div><small>TOTAL CASH RECEIVED</small><h3>ยอดรวมเงินที่ได้รับทั้งหมด</h3><strong>฿${baht(allCashReceived)}</strong><span>ดาวน์ ฿${baht(allDownPayments)} · เงินต้น ฿${baht(allPrincipalReceived)} · ดอกเบี้ย ฿${baht(allInterestReceived)}</span></div>
          </div>
          <div class="trade-progress"><i style="width:${clamp(percent)}%"></i></div>
          <div class="trade-status-rows"><span><i class="paid"></i><small>ชำระแล้ว</small><strong>฿${baht(summary.received)}</strong><em>${percent}%</em></span><span><i class="pending"></i><small>รอชำระ</small><strong>฿${baht(summary.pending)}</strong><em>${Math.max(0,100-percent)}%</em></span><span><i class="overdue"></i><small>ค้างชำระ</small><strong>${overdueCount} ราย</strong><em>RISK</em></span></div>
          <div class="portfolio-balance-card">
            <div class="portfolio-balance-donut" style="--progress:${clamp(portfolioPercent) * 3.6}deg"><div><strong>${portfolioPercent}%</strong><small>รับแล้ว</small></div></div>
            <div><small>ยอดเงินผ่อนคงเหลือทั้งระบบ</small><strong>฿${baht(summary.remaining)}</strong><span>รับแล้ว ฿${baht(summary.paid)} จาก ฿${baht(summary.financed)}</span></div>
          </div>
        </aside>
      </div>

      <section class="trade-interest-grid" aria-label="ภาพรวมดอกเบี้ย">
        <article class="interest-analytics-card received">
          <div class="interest-analytics-donut" style="--progress:${clamp(monthInterestPercent) * 3.6}deg"><div><strong>${monthInterestPercent}%</strong><small>ของเงินรับ</small></div></div>
          <div><p>INTEREST / MONTH</p><h3>ดอกเบี้ยที่ได้รับเดือนนี้</h3><strong>฿${baht(interest.receivedThisMonth)}</strong><span>เงินต้น ฿${baht(interest.principalThisMonth)} · รับรวม ฿${baht(summary.received)}</span></div>
        </article>
        <article class="interest-analytics-card outstanding">
          <div class="interest-analytics-donut" style="--progress:${clamp(outstandingInterestPercent) * 3.6}deg"><div><strong>฿${baht(interest.outstanding)}</strong><small>ยังไม่รับ</small></div></div>
          <div><p>FUTURE INTEREST</p><h3>ดอกเบี้ยที่ยังไม่ได้รับทั้งหมด</h3><strong>฿${baht(interest.outstanding)}</strong><span>รับแล้ว ฿${baht(interest.receivedLifetime)} จากดอกเบี้ยทั้งหมด ฿${baht(interest.total)}</span></div>
        </article>
      </section>

      <div class="trade-bottom-grid">
        <section class="trade-bar-panel"><div class="mini-panel-head"><div><p>MONTHLY VOLUME</p><h3>เปรียบเทียบเงินรับ</h3></div><span>6 เดือน</span></div><div class="trade-bars">${monthlyHeights.map((height,index)=>`<i class="${index===5?"current":""}" style="height:${height}%"><b>${compactMoney(monthlyIncome[index])}</b><small>${monthlyLabels[index]}</small></i>`).join("")}</div></section>
        <section class="trade-due-panel"><div class="mini-panel-head"><div><p>TODAY'S DUE</p><h3>ถึงกำหนดวันนี้</h3></div><strong>${dueToday.length}</strong></div><div class="trade-compact-list">${dueToday.length ? dueToday.slice(0,2).map(todayDueRow).join("") : `<div class="trade-clear"><span>✓</span><div><strong>ไม่มีรายการวันนี้</strong><small>ยังไม่มีลูกค้าถึงกำหนดชำระ</small></div></div>`}</div></section>
        <section class="trade-watch-panel"><div class="mini-panel-head"><div><p>WATCHLIST</p><h3>รายการต้องติดตาม</h3></div><button data-action="customers">ดูทั้งหมด</button></div><div class="trade-compact-list">${urgent.length ? urgent.slice(0,2).map(paymentRow).join("") : `<div class="trade-clear"><span>✓</span><div><strong>สถานะปกติ</strong><small>ไม่มีรายการค้างชำระ</small></div></div>`}</div></section>
      </div>
    </section>`;
}

function mobileDashboardView() {
  const summary = metrics();
  const interest = state.interestStats || {};
  const percent = summary.expected ? Math.round((summary.received / summary.expected) * 100) : 0;
  const portfolioPercent = summary.financed ? Math.round((summary.paid / summary.financed) * 100) : 0;
  const allDownPayments = state.contracts.reduce((sum, item) => sum + Number(item.downPayment || 0), 0);
  const allPrincipalReceived = state.contracts.reduce((sum, item) => sum + Number(item.paidPrincipal ?? item.paidTotal ?? 0), 0);
  const allInterestReceived = state.contracts.reduce((sum, item) => sum + Number(item.paidInterest || 0), 0);
  const allCashReceived = allDownPayments + allPrincipalReceived + allInterestReceived;
  const allContractCash = state.contracts.reduce((sum, item) => sum + Number(item.totalPrice || 0) + Number(item.totalInterest || 0), 0);
  const allCashPercent = allContractCash ? Math.round((allCashReceived / allContractCash) * 100) : 0;
  const monthInterestPercent = summary.received ? Math.round((Number(interest.receivedThisMonth || 0) / summary.received) * 100) : 0;
  const outstandingInterestPercent = Number(interest.total) ? Math.round((Number(interest.outstanding || 0) / Number(interest.total)) * 100) : 0;
  const today = new Date();
  const dueToday = state.contracts.filter((item) => item.status !== "waived" && Number(item.dueDay) === today.getDate());
  const urgent = state.contracts.filter((item) => ["overdue", "partial", "due"].includes(item.status));
  const overdueCount = state.contracts.filter((item) => item.status === "overdue").length;
  const income = [...(state.monthlyIncome || []).slice(-6)];
  while (income.length < 6) income.unshift(0);
  const labels = Array.from({ length: 6 }, (_, index) => new Intl.DateTimeFormat("th-TH", { month: "short" }).format(new Date(today.getFullYear(), today.getMonth() - 5 + index, 1)));
  const peak = Math.max(...income, 1);
  const points = income.map((amount, index) => `${18 + index * 56},${112 - Math.max(12, Math.round((amount / peak) * 86))}`).join(" ");
  return `
    <section class="screen mobile-dashboard">
      <header class="mobile-topbar">
        <button class="mobile-menu" data-action="customers" aria-label="เปิดรายชื่อลูกค้า">☰</button>
        <div class="mobile-brand"><span>GL</span><div><strong>GOLDEN LANDPAY</strong><small>MOBILE COLLECTION</small></div></div>
        <button class="mobile-settings" data-action="settings" aria-label="ตั้งค่า">•••</button>
      </header>

      <button class="mobile-workspace" data-action="switch-workspace"><span><small>พื้นที่ทำงาน</small><strong>${escapeHtml(activeWorkspace()?.name || "")}</strong></span><b>เปลี่ยน ›</b></button>

      <section class="mobile-hero">
        <div class="mobile-donut" style="--progress:${clamp(percent) * 3.6}deg"><div><strong>${state.contracts.length}</strong><small>แปลง</small></div></div>
        <div class="mobile-hero-copy"><span>ยอดรับเดือนนี้</span><strong><small>฿</small>${baht(summary.received)}</strong><em>${percent}% จากเป้าหมาย ฿${baht(summary.expected)}</em><div class="mobile-progress"><i style="width:${clamp(percent)}%"></i></div></div>
      </section>

      <section class="mobile-portfolio-balance">
        <div class="mobile-balance-donut" style="--progress:${clamp(portfolioPercent) * 3.6}deg"><div><strong>${portfolioPercent}%</strong><small>รับแล้ว</small></div></div>
        <div><small>PORTFOLIO BALANCE</small><h2>ยอดเงินผ่อนคงเหลือ</h2><strong>฿${baht(summary.remaining)}</strong><span>จากยอดผ่อนทั้งหมด ฿${baht(summary.financed)}</span></div>
      </section>

      <section class="mobile-grand-received">
        <div class="mobile-grand-received-donut" style="--progress:${clamp(allCashPercent) * 3.6}deg"><div><strong>${allCashPercent}%</strong><small>รับสะสม</small></div></div>
        <div><small>TOTAL CASH RECEIVED</small><h2>ยอดรวมเงินที่ได้รับทั้งหมด</h2><strong>฿${baht(allCashReceived)}</strong><span>ดาวน์ ฿${baht(allDownPayments)} · เงินต้น ฿${baht(allPrincipalReceived)} · ดอกเบี้ย ฿${baht(allInterestReceived)}</span></div>
      </section>

      <section class="mobile-interest-grid">
        <article><div class="mobile-interest-donut received" style="--progress:${clamp(monthInterestPercent) * 3.6}deg"><span>${monthInterestPercent}%</span></div><div><small>ดอกเบี้ยเดือนนี้</small><strong>฿${baht(interest.receivedThisMonth)}</strong><em>จากยอดรับ ฿${baht(summary.received)}</em></div></article>
        <article><div class="mobile-interest-donut outstanding" style="--progress:${clamp(outstandingInterestPercent) * 3.6}deg"><span>฿</span></div><div><small>ดอกเบี้ยที่ยังไม่ได้รับ</small><strong>฿${baht(interest.outstanding)}</strong><em>ทั้งหมด ฿${baht(interest.total)}</em></div></article>
      </section>

      <section class="mobile-kpis" aria-label="ภาพรวมการรับชำระ">
        <article class="gold"><span>◉</span><small>กำลังผ่อน</small><strong>${state.contracts.length}</strong><em>แปลง</em></article>
        <article class="cyan"><span>฿</span><small>ควรรับเดือนนี้</small><strong>${baht(summary.expected)}</strong><em>บาท</em></article>
        <article class="orange"><span>◷</span><small>ยังไม่ได้รับ</small><strong>${baht(summary.pending)}</strong><em>บาท</em></article>
        <article class="red"><span>!</span><small>ค้างชำระ</small><strong>${overdueCount}</strong><em>รายการ</em></article>
      </section>

      <section class="mobile-line-card">
        <header><div><small>CASH FLOW / 6M</small><h2>เงินที่ได้รับแต่ละเดือน</h2></div><strong>฿${baht(summary.received)}</strong></header>
        <div class="mobile-line-stage"><svg viewBox="0 0 300 125" preserveAspectRatio="none" aria-label="กราฟเงินรับย้อนหลังหกเดือน"><defs><linearGradient id="mobile-line" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="#36b9ff"/><stop offset=".7" stop-color="#8d6cff"/><stop offset="1" stop-color="#ffc531"/></linearGradient></defs><polyline points="${points}"/></svg></div>
        <div class="mobile-months">${labels.map((label, index) => `<span><b>฿${income[index] >= 1000 ? `${Math.round(income[index] / 1000)}k` : income[index]}</b><small>${label}</small></span>`).join("")}</div>
      </section>

      <section class="mobile-action-card">
        <header><div><small>TODAY'S DUE</small><h2>ถึงกำหนดวันนี้</h2></div><strong>${dueToday.length}</strong></header>
        <div class="mobile-list">${dueToday.length ? dueToday.slice(0, 3).map(todayDueRow).join("") : `<div class="mobile-empty"><span>✓</span><div><strong>ไม่มีรายการวันนี้</strong><small>ยังไม่มีลูกค้าถึงกำหนดชำระ</small></div></div>`}</div>
      </section>

      <section class="mobile-action-card mobile-watch">
        <header><div><small>WATCHLIST</small><h2>รายการต้องติดตาม</h2></div><button data-action="customers">ดูทั้งหมด</button></header>
        <div class="mobile-list">${urgent.length ? urgent.slice(0, 3).map(paymentRow).join("") : `<div class="mobile-empty"><span>✓</span><div><strong>สถานะปกติ</strong><small>ไม่มีรายการค้างชำระ</small></div></div>`}</div>
      </section>
    </section>`;
}

function paymentCalendar() {
  const now = new Date();
  const totalDays = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).getDay();
  const dueByDay = new Map();
  state.contracts.forEach((contract) => {
    if (contract.status === "waived") return;
    if (!dueByDay.has(Number(contract.dueDay))) dueByDay.set(Number(contract.dueDay), []);
    dueByDay.get(Number(contract.dueDay)).push(contract);
  });
  const cells = Array.from({ length: firstDay }, () => `<span class="calendar-day empty"></span>`);
  for (let day = 1; day <= totalDays; day += 1) {
    const contracts = dueByDay.get(day) || [];
    const status = contracts.some((item) => item.status === "overdue") ? "overdue" : contracts.some((item) => ["partial", "due"].includes(item.status)) ? "due" : contracts.some((item) => item.status === "paid") ? "paid" : contracts.length ? "upcoming" : "";
    cells.push(`<span class="calendar-day ${status} ${day === now.getDate() ? "today" : ""}" title="${contracts.length ? `${contracts.length} สัญญา` : "ไม่มีนัดชำระ"}"><b>${day}</b>${contracts.length ? `<i>${contracts.length}</i>` : ""}</span>`);
  }
  return `<div class="calendar-week"><span>อา</span><span>จ</span><span>อ</span><span>พ</span><span>พฤ</span><span>ศ</span><span>ส</span></div><div class="calendar-grid">${cells.join("")}</div>`;
}

function todayDueRow(item) {
  const remainingThisMonth = Math.max(0, Number(item.installmentAmount) - Number(item.monthPaid));
  return `<button class="today-due-row" data-action="open-contract" data-id="${item.id}">
    <span class="today-avatar ${item.tone}">${escapeHtml(item.initials)}</span>
    <span class="today-customer"><strong>${escapeHtml(item.customer)}</strong><small>แปลง ${escapeHtml(item.plot)} · ครบกำหนดวันนี้</small></span>
    <span class="today-payment"><strong>฿${baht(item.status === "paid" ? item.monthPaid : remainingThisMonth)}</strong><small class="status ${item.status}">${statusLabels[item.status]}</small></span>
    <b>›</b>
  </button>`;
}

function metricCard(label, value, unit, type, progress) {
  const icons = { estate: "⌂", paidstat: "✓", pending: "◷", alert: "!", balance: "฿" };
  return `<article class="metric-card ${type}"><div class="metric-top"><span>${icons[type]}</span><small>${escapeHtml(label)}</small></div><strong>${escapeHtml(value)}</strong>${unit ? `<em>${escapeHtml(unit)}</em>` : ""}<div class="metric-progress"><i style="width:${clamp(progress)}%"></i></div></article>`;
}

function paymentRow(item) {
  return `<button class="list-row" data-action="open-contract" data-id="${item.id}"><span class="avatar ${item.tone}">${escapeHtml(item.initials)}</span><span class="row-copy"><strong>${escapeHtml(item.customer)}</strong><small>แปลง ${escapeHtml(item.plot)} · ทุกวันที่ ${item.dueDay}</small></span><span class="row-money"><strong>฿${baht(item.installmentAmount - item.monthPaid)}</strong><small class="status ${item.status}">${statusLabels[item.status]}</small></span><span class="chevron">›</span></button>`;
}

function customersView() {
  const search = state.search.toLowerCase();
  const items = state.contracts.filter((item) => {
    const matchesSearch = `${item.customer} ${item.phone} ${item.plot} ${item.contractNo}`.toLowerCase().includes(search);
    const matchesFilter = state.filter === "all" || (state.filter === "paid" ? item.status === "paid" : ["overdue", "partial", "due"].includes(item.status));
    return matchesSearch && matchesFilter;
  });
  return `
    <section class="screen customers-screen">
      ${compactBrandBar("home")}
      <header class="screen-titlebar"><div><p class="overline">ทะเบียนสัญญา</p><h2>ลูกค้าและแปลง</h2></div><button class="add-button" data-action="add" aria-label="เพิ่มลูกค้า">＋</button></header>
      <label class="search-box"><span>⌕</span><input id="customer-search" value="${escapeHtml(state.search)}" placeholder="ค้นหาชื่อ เบอร์โทร หรือเลขแปลง"><button data-action="clear-search" aria-label="ล้างการค้นหา">×</button></label>
      <div class="filter-pills"><button class="${state.filter === "all" ? "active" : ""}" data-action="filter" data-filter="all">ทั้งหมด <span>${state.contracts.length}</span></button><button class="${state.filter === "attention" ? "active" : ""}" data-action="filter" data-filter="attention">ต้องติดตาม</button><button class="${state.filter === "paid" ? "active" : ""}" data-action="filter" data-filter="paid">ชำระแล้ว</button></div>
      <div class="contract-list">
        ${items.map(contractCard).join("") || `<div class="empty-state framed">ไม่พบลูกค้าตามที่ค้นหา</div>`}
      </div>
    </section>`;
}

function contractCard(item) {
  const financed = item.totalPrice - item.downPayment;
  const progress = clamp(Math.round((Number(item.paidPrincipal ?? item.paidTotal) / financed) * 100));
  const skipped = Number(item.skippedInstallments || 0);
  const remainingInstallments = Math.max(0, item.totalInstallments - item.paidInstallments - skipped);
  const actionLabel = item.status === "paid" ? "ดูสลิปงวดเดือนนี้" : item.status === "partial" ? "ชำระยอดที่เหลือเต็มงวด" : item.status === "waived" ? "เดือนนี้อนุญาตข้ามงวดแล้ว" : "ชำระงวดถัดไป";
  const quickAction = item.status === "waived" ? "open-contract" : "quick-payment";
  return `<article class="contract-card">
    <button class="contract-card-main" data-action="open-contract" data-id="${item.id}">
      <div class="contract-top"><div class="mini-ring ${item.tone}" style="--progress:${progress * 3.6}deg"><span>${progress}%</span></div><span class="row-copy"><strong>${escapeHtml(item.customer)}</strong><small>${escapeHtml(item.phone)}</small></span><span class="status ${item.status}">${statusLabels[item.status]}</span></div>
      <div class="plot-meta"><span>แปลง <strong>${escapeHtml(item.plot)}</strong></span><span>${escapeHtml(item.contractNo)}</span></div>
      <div class="loading-bar contract-progress"><i style="width:${progress}%"></i></div>
      <div class="contract-stats"><span><small>เงินต้นรับแล้ว</small><strong>฿${baht(item.paidPrincipal ?? item.paidTotal)}</strong></span><span><small>เงินต้นคงเหลือ</small><strong>฿${baht(financed - Number(item.paidPrincipal ?? item.paidTotal))}</strong></span><span><small>เหลืออีก</small><strong>${remainingInstallments} งวด</strong></span></div>
    </button>
    <button class="quick-payment-button ${item.status}" data-action="${quickAction}" data-id="${item.id}"><span>${item.status === "paid" ? "✓" : item.status === "waived" ? "↷" : "฿"}</span>${actionLabel}<b>›</b></button>
  </article>`;
}

function contractView() {
  const contract = selectedContract();
  if (!contract) return `<section class="screen"><div class="empty-state">ยังไม่มีข้อมูลสัญญา</div></section>`;
  const financed = contract.totalPrice - contract.downPayment;
  const installments = state.installments[contract.id] || buildInstallments(contract);
  const skippedInstallments = Number(contract.skippedInstallments ?? installments.filter((item) => item.status === "waived").length);
  const paidPrincipal = Number(contract.paidPrincipal ?? contract.paidTotal);
  const paidInterest = Number(contract.paidInterest || 0);
  const totalInterest = Number(contract.totalInterest || 0);
  const remainingInterest = Math.max(0, totalInterest - paidInterest);
  const remaining = Math.max(0, financed - paidPrincipal);
  const totalReceived = Number(contract.downPayment) + paidPrincipal;
  const totalRemaining = Math.max(0, Number(contract.totalPrice) - totalReceived);
  const totalProgress = contract.totalPrice ? clamp(Math.round((totalReceived / contract.totalPrice) * 100)) : 0;
  const installmentProgress = financed ? clamp(Math.round((paidPrincipal / financed) * 100)) : 0;
  const installmentCountProgress = contract.totalInstallments ? clamp(Math.round((contract.paidInstallments / contract.totalInstallments) * 100)) : 0;
  const interestProgress = totalInterest ? clamp(Math.round((paidInterest / totalInterest) * 100)) : 0;
  const grandTotal = Number(contract.totalPrice) + totalInterest;
  const grandReceived = Number(contract.downPayment) + paidPrincipal + paidInterest;
  const grandRemaining = Math.max(0, grandTotal - grandReceived);
  const grandProgress = grandTotal ? clamp(Math.round((grandReceived / grandTotal) * 100)) : 0;
  const payableRemaining = Math.max(0, contract.totalInstallments - contract.paidInstallments - skippedInstallments);
  const prepayments = state.prepayments[contract.id] || [];
  const prepaidTotal = prepayments.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const historicalInstallments = installments.filter(isHistoricalPayableInstallment);
  const currentInstallment = firstOpenInstallment(installments);
  const skipLocked = isInstallmentLocked(currentInstallment?.dueDate);
  const payoffAmount = calculatePayoff(remaining, Number(contract.interestAmount || 0));
  const filter = state.installmentFilter || "all";
  const filteredInstallments = installments.filter((item) => filter === "all" || (filter === "open" ? !["paid", "waived"].includes(item.status) : item.status === filter));
  const pageSize = 12;
  const pageCount = Math.max(1, Math.ceil(filteredInstallments.length / pageSize));
  const currentPage = Math.min(pageCount, Math.max(1, Number(state.installmentPage) || 1));
  const visibleInstallments = filteredInstallments.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const editLocked = Number(contract.paidTotal) > 0 || Number(contract.paidInstallments) > 0;
  return `
    <section class="screen detail-screen">
      ${compactBrandBar("back-customers")}
      <div class="contract-number-line"><small>รายละเอียดสัญญา</small><strong>${escapeHtml(contract.contractNo)}</strong></div>
      <div class="customer-identity"><span class="avatar large ${contract.tone}">${escapeHtml(contract.initials)}</span><div><h2>${escapeHtml(contract.customer)}</h2><p>แปลง ${escapeHtml(contract.plot)} · ผ่อนทุกวันที่ ${contract.dueDay}</p></div><div class="customer-identity-actions"><a href="tel:${contract.phone.replaceAll(" ", "")}">โทร</a>${editLocked ? `<button type="button" class="edit-locked" disabled title="แปลงนี้เคยรับเงินค่างวดแล้ว"><span>▣</span> รับเงินแล้ว · ล็อก</button>` : `<button type="button" data-action="edit-contract"><span>✎</span> แก้ไขทุกข้อมูล</button>`}</div></div>

      <article class="contract-hero contract-analytics-hero">
        <div class="contract-watermark">GL</div>
        <div class="contract-ring-grid">
          <section class="contract-ring-card main grand-total-card">
            <div class="contract-data-ring grand-total" style="--progress:${grandProgress * 3.6}deg"><div><strong>${grandProgress}%</strong><small>ยอดรวม</small></div></div>
            <div class="contract-ring-copy"><small>ยอดรวมเงินต้นและดอกเบี้ยทั้งสัญญา</small><strong>฿${baht(grandReceived)}</strong><span>รับแล้วจากยอดรวม ฿${baht(grandTotal)}</span><em>คงเหลือรวม ฿${baht(grandRemaining)}</em></div>
          </section>
          <section class="contract-ring-card land-total-card">
            <div class="contract-data-ring total" style="--progress:${totalProgress * 3.6}deg"><div><strong>${totalProgress}%</strong><small>ยอดรวม</small></div></div>
            <div class="contract-ring-copy"><small>ราคาที่ดินรวมเงินดาวน์</small><strong>฿${baht(totalReceived)}</strong><span>รับแล้วจาก ฿${baht(contract.totalPrice)}</span><em>คงเหลือ ฿${baht(totalRemaining)}</em></div>
          </section>
          <section class="contract-ring-card">
            <div class="contract-data-ring installment" style="--progress:${installmentProgress * 3.6}deg"><div><strong>${installmentProgress}%</strong><small>เงินผ่อน</small></div></div>
            <div class="contract-ring-copy"><small>เงินต้นในยอดผ่อนทั้งหมด</small><strong>฿${baht(paidPrincipal)}</strong><span>รับแล้วจาก ฿${baht(financed)}</span><em>เหลือ ฿${baht(remaining)}</em></div>
          </section>
          <section class="contract-ring-card">
            <div class="contract-data-ring count" style="--progress:${installmentCountProgress * 3.6}deg"><div><strong>${contract.paidInstallments}</strong><small>/${contract.totalInstallments}</small></div></div>
            <div class="contract-ring-copy"><small>จำนวนงวดตามจริง</small><strong>${contract.paidInstallments} งวด</strong><span>รอชำระ ${payableRemaining} งวด</span><em>ข้าม ${skippedInstallments} งวด</em></div>
          </section>
          <section class="contract-ring-card interest-card">
            <div class="contract-data-ring interest" style="--progress:${interestProgress * 3.6}deg"><div><strong>${interestProgress}%</strong><small>ดอกเบี้ย</small></div></div>
            <div class="contract-ring-copy"><small>ดอกเบี้ยทั้งหมดในสัญญา</small><strong>฿${baht(paidInterest)}</strong><span>รับแล้วจาก ฿${baht(totalInterest)}</span><em>เหลือ ฿${baht(remainingInterest)}</em></div>
          </section>
        </div>
      </article>

      <div class="contract-side-stack">
        <article class="payoff-card">
          <div><p class="overline">ยอดปิดสัญญาวันนี้</p><h3>฿${baht(payoffAmount)}</h3><small>เงินต้นคงเหลือ ฿${baht(remaining)} ${remaining > 0 && Number(contract.interestAmount) > 0 ? `+ ดอกเบี้ย 1 เดือน ฿${baht(contract.interestAmount)}` : "· ไม่คิดดอกเบี้ยเพิ่ม"}</small></div>
          <span><small>ดอกเบี้ยต่องวด</small><strong>฿${baht(contract.interestAmount)}</strong><em>${contract.interestMode === "percent" ? `${contract.interestRate}% ต่อเดือน` : contract.interestMode === "fixed" ? "จำนวนเงินคงที่" : "ไม่คิดดอกเบี้ย"}</em></span>
        </article>

        ${currentInstallment ? `<article class="due-card">
          <div><p class="overline">งวดปัจจุบัน</p><h3>งวดที่ ${currentInstallment.no}</h3></div><div class="date-medallion"><strong>${new Date(currentInstallment.dueDate).getDate()}</strong><small>${new Intl.DateTimeFormat("th-TH", { month: "short" }).format(currentInstallment.dueDate)}</small></div>
          <div class="due-amount"><span>ยอดที่ต้องชำระเต็มงวด</span><strong>฿${baht(Math.max(0, currentInstallment.amount - currentInstallment.paid))}</strong><small>เงินต้น ฿${baht(Math.max(0, currentInstallment.principal - currentInstallment.principalPaid))} · ดอกเบี้ย ฿${baht(Math.max(0, currentInstallment.interest - currentInstallment.interestPaid))}</small></div>
          <div class="due-actions"><button class="button button-dark" data-action="payment" data-number="${currentInstallment.no}">บันทึกการชำระเต็มงวด</button><button class="button skip-installment-button" data-action="skip-installment" data-number="${currentInstallment.no}" ${skipLocked || currentInstallment.status === "partial" || currentInstallment.paid > 0 ? "disabled" : ""}>อนุญาตข้ามงวดนี้</button></div>
          <p class="available-note">ชำระล่วงหน้าได้ เมื่อบันทึกแล้วงวดถัดไปจะเปิดทันที${skipLocked ? ` · ข้ามงวดได้วันที่ ${shortDate(installmentUnlockDate(currentInstallment.dueDate))}` : ""}</p>
        </article>` : `<article class="due-card contract-complete-card"><span>✓</span><div><p class="overline">ปิดยอดครบแล้ว</p><h3>สัญญานี้ชำระครบทุกงวด</h3><small>ไม่มีงวดที่ต้องชำระต่อ</small></div></article>`}

        <article class="principal-prepayment-card">
          <span class="principal-prepayment-icon">↘</span>
          <div><p class="overline">ลดเงินต้น · ลดจำนวนงวด</p><h3>โปะเงินต้นเพิ่มเติม</h3><small>${prepaidTotal > 0 ? `โปะสะสมแล้ว ฿${baht(prepaidTotal)} · ${prepayments.length} รายการ` : "ค่างวดและดอกเบี้ยต่องวดยังคงเดิม"}</small></div>
          <button data-action="principal-prepayment" ${remaining <= 0 ? "disabled" : ""}>${remaining <= 0 ? "ปิดต้นแล้ว" : "บันทึกยอดโปะได้ตลอด"}<b>›</b></button>
        </article>

        <article class="historical-payment-card ${historicalInstallments.length ? "has-history" : "is-clear"}">
          <span class="historical-payment-icon">↶</span>
          <div><p class="overline">บันทึกข้อมูลจากระบบเดิม</p><h3>ชำระงวดย้อนหลัง</h3><small>${historicalInstallments.length ? `มี ${historicalInstallments.length} งวดที่ผ่านมาให้บันทึกพร้อมสลิป` : "ไม่มีงวดค้างย้อนหลังในขณะนี้"}</small></div>
          <button data-action="historical-payments" ${historicalInstallments.length ? "" : "disabled"}>${historicalInstallments.length ? "เลือกงวด" : "ครบแล้ว"}<b>›</b></button>
        </article>
      </div>

      <section class="content-section installment-history-section"><div class="section-heading"><div><p class="overline">ประวัติและกำหนดการ</p><h3>ตารางงวด</h3></div><span class="count-chip">${contract.totalInstallments} งวด</span></div>
        <div class="installment-filters"><button class="${filter === "all" ? "active" : ""}" data-action="installment-filter" data-filter="all">ทั้งหมด</button><button class="${filter === "open" ? "active" : ""}" data-action="installment-filter" data-filter="open">รอชำระ</button><button class="${filter === "paid" ? "active" : ""}" data-action="installment-filter" data-filter="paid">ชำระแล้ว</button><button class="${filter === "waived" ? "active" : ""}" data-action="installment-filter" data-filter="waived">ข้ามงวด</button></div>
        <div class="list-card installment-list">${visibleInstallments.map((item) => installmentRow(item, isActionableInstallment(item, installments), prepayments.filter((payment) => Number(payment.installmentNo) === Number(item.no)))).join("") || `<div class="empty-state">ไม่พบงวดในสถานะนี้</div>`}</div>
        ${pageCount > 1 ? `<div class="installment-pagination"><button data-action="installment-page" data-page="${currentPage - 1}" ${currentPage === 1 ? "disabled" : ""}>‹ ก่อนหน้า</button><span>หน้า ${currentPage} จาก ${pageCount}<small>แสดงครั้งละ ${pageSize} งวด</small></span><button data-action="installment-page" data-page="${currentPage + 1}" ${currentPage === pageCount ? "disabled" : ""}>ถัดไป ›</button></div>` : ""}
      </section>
      <div class="delete-customer-zone"><button class="delete-customer-button" data-action="delete-customer"><span>⌫</span> ลบลูกค้าออก</button></div>
    </section>`;
}

function offerView() {
  const draft = state.offerDraft;
  const plan = calculateContractPlan(draft);
  const inputValue = (value) => value === "" || value == null ? "" : Number(value);
  return `<section class="screen offer-screen">
    <header class="offer-header">
      <div class="offer-brand"><span>GL</span><div><small>SALES SIMULATOR</small><strong>เสนอขายที่ดิน</strong></div></div>
      <div class="offer-header-copy"><p>คำนวณแผนผ่อนก่อนเสนอราคา</p><small>เป็นพื้นที่ทดลองเท่านั้น ข้อมูลหน้านี้จะไม่บันทึกลงในรายชื่อลูกค้า</small></div>
    </header>
    <form id="offer-calculator-form" class="offer-layout" novalidate>
      <section class="offer-input-card">
        <div class="offer-section-head"><span>01</span><div><small>กรอกเงื่อนไข</small><h2>กำหนดราคาและค่างวด</h2></div></div>
        <div class="offer-field-grid">
          <label class="offer-field-wide">ราคาเสนอขาย<input name="totalPrice" type="number" min="1" step="0.01" inputmode="decimal" placeholder="กรอกราคาขาย" value="${inputValue(draft.totalPrice)}"><small>ราคารวมเงินดาวน์ แต่ยังไม่รวมดอกเบี้ย</small></label>
          <label>เงินดาวน์<input name="downPayment" type="number" min="0" step="0.01" inputmode="decimal" placeholder="กรอกเงินดาวน์" value="${inputValue(draft.downPayment)}"></label>
          <label>ยอดที่ลูกค้าจ่ายต่องวด<input name="installmentAmount" type="number" min="0.01" step="0.01" inputmode="decimal" placeholder="กรอกค่างวด" value="${inputValue(draft.installmentAmount)}"></label>
        </div>
        ${interestFields(draft, { blank: true })}
        <div class="offer-rule-note"><span>i</span><p>ระบบยึดค่างวดตามที่ระบุ หักดอกเบี้ยต่องวดก่อน ส่วนที่เหลือจึงนำไปลดเงินต้น และงวดสุดท้ายคิดเงินต้นตามจริงบวกดอกเบี้ยเต็มงวด</p></div>
        <button class="offer-reset-button" type="button" data-action="reset-offer">↻ เริ่มคำนวณใหม่</button>
      </section>
      <section class="offer-results" data-offer-results>${offerResultsMarkup(plan, draft)}</section>
    </form>
  </section>`;
}

function offerResultsMarkup(plan, draft) {
  const isEmpty = [draft.totalPrice, draft.downPayment, draft.installmentAmount].every((value) => value === "" || value == null);
  if (isEmpty) return `<article class="offer-invalid-card offer-empty-card"><span>◇</span><div><small>พร้อมคำนวณ</small><strong>กรอกตัวเลขเพื่อดูแผนการผ่อน</strong><p>หน้านี้ใช้ทดลองคำนวณเท่านั้น และจะไม่บันทึกข้อมูลลงในรายชื่อลูกค้า</p></div></article>`;
  const totalPrice = Math.max(0, Number(draft.totalPrice) || 0);
  const downPayment = Math.max(0, Number(draft.downPayment) || 0);
  const installmentAmount = Math.max(0, Number(draft.installmentAmount) || 0);
  const invalidPrice = totalPrice <= 0 || downPayment >= totalPrice;
  const invalidInstallment = plan.monthlyPrincipal <= 0;
  const valid = !invalidPrice && !invalidInstallment && plan.totalInstallments > 0;
  const totalPayable = valid ? totalPrice + plan.totalInterest : 0;
  const downPercent = totalPrice > 0 ? clamp(Math.round((downPayment / totalPrice) * 100)) : 0;
  const years = Math.floor(plan.totalInstallments / 12);
  const months = plan.totalInstallments % 12;
  const duration = `${baht(plan.totalInstallments)} งวด${years ? ` · ${years} ปี` : ""}${months ? ` ${months} เดือน` : ""}`;
  const warning = invalidPrice ? "ราคาขายต้องมากกว่าเงินดาวน์" : invalidInstallment ? "ค่างวดต้องมากกว่าดอกเบี้ยต่องวด" : "";
  if (!valid) return `<article class="offer-invalid-card"><span>!</span><div><small>ยังคำนวณไม่ได้</small><strong>${warning}</strong><p>ตรวจสอบตัวเลขด้านซ้าย แล้วผลลัพธ์จะเปลี่ยนให้อัตโนมัติ</p></div></article>`;
  return `<article class="offer-total-hero">
      <div class="offer-total-ring" style="--progress:${downPercent * 3.6}deg"><div><strong>${downPercent}%</strong><small>เงินดาวน์</small></div></div>
      <div class="offer-total-copy"><small>ยอดรวมที่ลูกค้าชำระทั้งสัญญา</small><strong>฿${baht(totalPayable)}</strong><span>ราคาที่ดิน ฿${baht(totalPrice)} + ดอกเบี้ย ฿${baht(plan.totalInterest)}</span><em>${duration}</em></div>
    </article>
    <div class="offer-metrics">
      <article><span>◒</span><small>เงินต้นจัดผ่อน</small><strong>฿${baht(plan.financedPrincipal)}</strong><em>หลังหักเงินดาวน์</em></article>
      <article><span>↘</span><small>เงินต้นลดต่องวด</small><strong>฿${baht(plan.monthlyPrincipal)}</strong><em>จากค่างวด ฿${baht(installmentAmount)}</em></article>
      <article><span>＋</span><small>ดอกเบี้ยต่องวด</small><strong>฿${baht(plan.monthlyInterest)}</strong><em>ดอกเบี้ยรวม ฿${baht(plan.totalInterest)}</em></article>
      <article><span>◎</span><small>จำนวนงวดทั้งหมด</small><strong>${baht(plan.totalInstallments)} งวด</strong><em>${years || months ? `${years} ปี ${months} เดือน` : "น้อยกว่า 1 เดือน"}</em></article>
    </div>
    <article class="offer-payment-flow">
      <header><div><small>PAYMENT FLOW</small><strong>ภาพรวมเส้นทางการชำระ</strong></div><span>ค่างวดคงที่</span></header>
      <div class="offer-flow-track"><i></i><i></i><i></i></div>
      <div class="offer-flow-labels"><span><small>เริ่มต้น</small><strong>ดาวน์ ฿${baht(downPayment)}</strong></span><span><small>ผ่อนรายเดือน</small><strong>${baht(plan.totalInstallments - 1)} × ฿${baht(installmentAmount)}</strong></span><span><small>งวดสุดท้าย</small><strong>฿${baht(plan.finalInstallment)}</strong></span></div>
    </article>`;
}

function updateOfferCalculator(form) {
  const numberOrBlank = (field) => field?.value.trim() === "" ? "" : Number(field.value);
  state.offerDraft = {
    totalPrice: numberOrBlank(form?.elements.totalPrice),
    downPayment: numberOrBlank(form?.elements.downPayment),
    installmentAmount: numberOrBlank(form?.elements.installmentAmount),
    interestMode: form?.elements.interestMode?.value || "none",
    interestRate: numberOrBlank(form?.elements.interestRate),
    interestAmount: numberOrBlank(form?.elements.interestAmount),
  };
  const results = form?.querySelector("[data-offer-results]");
  if (results) results.innerHTML = offerResultsMarkup(calculateContractPlan(state.offerDraft), state.offerDraft);
}

function compactBrandBar(backAction = "home") {
  return `<header class="compact-brandbar">
    <button class="compact-back" data-action="${backAction}" aria-label="ย้อนกลับ">‹</button>
    <div class="compact-wordmark"><span class="wing-fallback">≋</span><strong>GOLDEN LANDPAY</strong></div>
    <button class="compact-more" data-action="settings" aria-label="ตัวเลือกเพิ่มเติม">•••</button>
  </header>`;
}

function installmentRow(item, actionable = false, prepayments = []) {
  const action = item.status === "waived" ? "waived-installment" : "payment";
  const locked = !["paid", "waived"].includes(item.status) && !actionable;
  const prepaymentTotal = prepayments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
  const marker = prepayments.length ? `<span class="installment-prepayment-marker"><b>↘</b> โป๊ะ ${prepayments.length} ครั้ง · ฿${baht(prepaymentTotal)}</span>` : "";
  const sublist = prepayments.length ? `<div class="installment-prepayment-sublist" aria-label="รายการโปะของงวดที่ ${item.no}">${prepayments.map((payment) => `<article class="installment-prepayment-item"><span>↘</span><div><small>โปะเงินต้น · ${fullDate(payment.paidAt)}</small><strong>฿${baht(payment.amount)}</strong></div><div>${payment.slipUrl ? `<a href="${escapeHtml(payment.slipUrl)}" target="_blank" rel="noopener">เปิดสลิป</a>` : `<em>ไม่มีสลิป</em>`}<button type="button" data-action="replace-prepayment-slip" data-payment-id="${payment.id}">เปลี่ยนรูป</button></div></article>`).join("")}</div>` : "";
  return `<article class="installment-entry ${item.status} ${prepayments.length ? "has-prepayment" : ""}"><button class="list-row ${item.status} ${locked ? "sequence-locked" : ""}" data-action="${action}" data-number="${item.no}" ${locked ? "disabled" : ""}><span class="timeline ${item.status}">${item.status === "paid" ? "✓" : item.status === "waived" ? "↷" : item.no}</span><span class="row-copy"><strong>งวดที่ ${item.no}</strong><small>${shortDate(item.dueDate)}${item.interest ? ` · ดอกเบี้ย ฿${baht(item.interest)}` : ""}</small>${marker}</span><span class="row-money"><strong>${item.status === "waived" ? "พักงวด" : `฿${baht(item.amount)}`}</strong><small class="status ${item.status}">${locked ? "รอตามลำดับ" : statusLabels[item.status]}</small></span><span class="chevron">›</span></button>${sublist}</article>`;
}

function bottomNav(active) {
  return `<nav class="bottom-nav" aria-label="เมนูหลัก"><button class="${active === "dashboard" ? "active" : ""}" data-action="home"><span>⌂</span><small>หน้าหลัก</small></button><button class="${active === "offer" ? "active" : ""}" data-action="offer"><span>◇</span><small>เสนอขาย</small></button><button class="nav-add" data-action="add" aria-label="เพิ่มข้อมูล">＋</button><button class="${active === "customers" ? "active" : ""}" data-action="customers"><span>◎</span><small>ลูกค้า</small></button></nav>`;
}

function interestFields(contract = {}, options = {}) {
  const mode = contract.interestMode || "none";
  const blankRate = options.blank && (contract.interestRate === "" || contract.interestRate == null);
  const blankAmount = options.blank && (contract.interestAmount === "" || contract.interestAmount == null);
  return `<div class="interest-builder" data-interest-builder>
    <label>รูปแบบดอกเบี้ย<select name="interestMode">
      <option value="none" ${mode === "none" ? "selected" : ""}>ไม่คิดดอกเบี้ย</option>
      <option value="percent" ${mode === "percent" ? "selected" : ""}>เปอร์เซ็นต์คงที่ต่อเดือน</option>
      <option value="fixed" ${mode === "fixed" ? "selected" : ""}>ระบุจำนวนเงินดอกเบี้ยต่องวด</option>
    </select></label>
    <label data-interest-percent ${mode === "percent" ? "" : "hidden"}>ดอกเบี้ยต่อเดือน (%)<input name="interestRate" type="number" min="0.0001" max="100" step="0.0001" inputmode="decimal" placeholder="กรอกเปอร์เซ็นต์" value="${blankRate ? "" : Number(contract.interestRate || 0.25)}"><small class="field-help">คิดคงที่จากเงินต้นเริ่มต้นตลอดสัญญา</small></label>
    <label data-interest-fixed ${mode === "fixed" ? "" : "hidden"}>ดอกเบี้ยต่องวด (บาท)<input name="interestAmount" type="number" min="0.01" step="0.01" inputmode="decimal" placeholder="กรอกดอกเบี้ย" value="${blankAmount ? "" : Number(contract.interestAmount || 0)}"></label>
  </div>`;
}

function syncInterestFields(form) {
  const mode = form?.elements.interestMode?.value || "none";
  form?.querySelector("[data-interest-percent]")?.toggleAttribute("hidden", mode !== "percent");
  form?.querySelector("[data-interest-fixed]")?.toggleAttribute("hidden", mode !== "fixed");
  if (form?.id === "offer-calculator-form") updateOfferCalculator(form);
  else updateContractPlanPreview(form);
}

function contractPlanFromForm(form) {
  return calculateContractPlan({
    totalPrice: Number(form?.elements.totalPrice?.value || 0),
    downPayment: Number(form?.elements.downPayment?.value || 0),
    installmentAmount: Number(form?.elements.installmentAmount?.value || 0),
    interestMode: form?.elements.interestMode?.value || "none",
    interestRate: Number(form?.elements.interestRate?.value || 0),
    interestAmount: Number(form?.elements.interestAmount?.value || 0),
  });
}

const MAX_CONTRACT_INSTALLMENTS = 5000;

function contractFormError(form, message = "", fieldName = "") {
  const errorBox = form?.querySelector("[data-contract-form-error]");
  if (errorBox) {
    errorBox.textContent = message;
    errorBox.toggleAttribute("hidden", !message);
  }
  if (!message || !fieldName) return;
  const field = form?.elements?.namedItem(fieldName);
  if (field instanceof HTMLElement) {
    field.scrollIntoView({ behavior: "smooth", block: "center" });
    window.setTimeout(() => field.focus({ preventScroll: true }), 220);
  }
}

function validateContractForm(form) {
  const data = Object.fromEntries(new FormData(form));
  const plan = contractPlanFromForm(form);
  const customer = String(data.customer || "").trim();
  const plot = String(data.plot || "").trim();
  const dueDay = Number(data.dueDay);
  const totalPrice = Number(data.totalPrice);
  const downPayment = Number(data.downPayment);
  const installmentAmount = Number(data.installmentAmount);
  const interestMode = String(data.interestMode || "none");
  const interestRate = Number(data.interestRate);
  const interestAmount = Number(data.interestAmount);
  let error = null;

  if (!customer) error = ["กรุณากรอกชื่อลูกค้าก่อนบันทึก", "customer"];
  else if (!plot) error = ["กรุณากรอกเลขแปลงก่อนบันทึก", "plot"];
  else if (!Number.isInteger(dueDay) || dueDay < 1 || dueDay > 28) error = ["วันชำระต้องอยู่ระหว่างวันที่ 1–28", "dueDay"];
  else if (!String(data.start_date || "")) error = ["กรุณาเลือกเดือนเริ่มผ่อน", "start_month"];
  else if (!Number.isFinite(totalPrice) || totalPrice <= 0) error = ["กรุณากรอกราคาขายให้มากกว่า 0 บาท", "totalPrice"];
  else if (!Number.isFinite(downPayment) || downPayment < 0) error = ["เงินดาวน์ต้องไม่น้อยกว่า 0 บาท", "downPayment"];
  else if (downPayment >= totalPrice) error = ["ราคาขายต้องมากกว่าเงินดาวน์", "downPayment"];
  else if (!Number.isFinite(installmentAmount) || installmentAmount <= 0) error = ["กรุณากรอกยอดต่องวดให้มากกว่า 0 บาท", "installmentAmount"];
  else if (interestMode === "percent" && (!Number.isFinite(interestRate) || interestRate <= 0 || interestRate > 100)) error = ["กรุณาระบุดอกเบี้ยต่อเดือนให้ถูกต้อง", "interestRate"];
  else if (interestMode === "fixed" && (!Number.isFinite(interestAmount) || interestAmount <= 0)) error = ["กรุณาระบุจำนวนเงินดอกเบี้ยต่องวด", "interestAmount"];
  else if (plan.monthlyPrincipal <= 0) error = ["ยอดต่องวดต้องมากกว่าดอกเบี้ยต่องวด", "installmentAmount"];
  else if (plan.totalInstallments < 1 || plan.totalInstallments > MAX_CONTRACT_INSTALLMENTS) {
    const minimumInstallment = Math.ceil((plan.financedPrincipal / MAX_CONTRACT_INSTALLMENTS + plan.monthlyInterest) * 100) / 100;
    error = [`จำนวนงวด ${baht(plan.totalInstallments)} งวดเกินกว่าที่ระบบรองรับ กรุณาใส่ยอดต่องวดอย่างน้อย ฿${baht(minimumInstallment)}`, "installmentAmount"];
  }

  if (error) {
    contractFormError(form, error[0], error[1]);
    throw new Error(error[0]);
  }
  contractFormError(form);
  return { data, plan };
}

function planPreviewMarkup(plan) {
  return `<div><span>เงินต้นจัดผ่อน</span><strong>฿${baht(plan.financedPrincipal)}</strong></div><div><span>เงินต้นลดต่องวด</span><strong>฿${baht(plan.monthlyPrincipal)}</strong></div><div><span>ดอกเบี้ยต่องวด</span><strong>฿${baht(plan.monthlyInterest)}</strong></div><div><span>จำนวนงวด</span><strong>${baht(plan.totalInstallments)} งวด</strong></div>`;
}

function showAddModal() {
  showModal("เพิ่มลูกค้าและสัญญา", "กรอกครั้งเดียว ระบบจะสร้างตารางงวดให้อัตโนมัติ", `
    <form id="add-contract-form" class="form-stack modal-form" novalidate>
      <label>ชื่อลูกค้า<input name="customer" required placeholder="ชื่อ–นามสกุล"></label>
      <label>เบอร์โทร<input name="phone" inputmode="tel" placeholder="08x xxx xxxx"></label>
      <div class="field-pair"><label>เลขแปลง<input name="plot" required placeholder="เช่น A-01"></label><label>วันชำระ<input name="dueDay" required type="number" min="1" max="28" value="5"></label></div>
      <label>เดือนเริ่มผ่อน (พ.ศ.)${thaiStartMonthFields()}<small class="field-help">กำหนดครั้งเดียว ระบบจะรันงวดต่อให้อัตโนมัติเดือนละครั้งตามวันชำระ</small></label>
      <label>ราคาขาย<input name="totalPrice" required type="number" inputmode="decimal" placeholder="0 บาท"></label>
      <div class="field-pair"><label>เงินดาวน์<input name="downPayment" required type="number" inputmode="decimal" value="0"></label><label>ยอดต่องวด<input name="installmentAmount" required type="number" inputmode="decimal" placeholder="0"></label></div>
      ${interestFields()}
      <div class="edit-schedule-preview contract-plan-preview" data-contract-plan-preview>${planPreviewMarkup(calculateContractPlan({ totalPrice: 0, downPayment: 0, installmentAmount: 0 }))}</div>
      <p class="contract-form-error" data-contract-form-error role="alert" hidden></p>
      <button class="button button-gold" type="submit">บันทึกและสร้างตารางงวด</button>
    </form>`);
}

function showEditContractModal() {
  const contract = selectedContract();
  if (!contract) return;
  if (Number(contract.paidTotal) > 0 || Number(contract.paidInstallments) > 0) {
    showToast("แปลงนี้เคยรับเงินค่างวดแล้ว จึงไม่สามารถแก้ไขข้อมูลได้");
    return;
  }
  const phone = contract.phone === "ยังไม่ระบุ" ? "" : contract.phone;
  const startDate = contract.startDate ? new Date(`${contract.startDate}T00:00:00`) : new Date();
  showModal("แก้ไขข้อมูลลูกค้าและแปลง", `สัญญา ${contract.contractNo}`, `
    <form id="edit-contract-form" class="form-stack modal-form" data-id="${contract.id}" novalidate>
      <label>ชื่อลูกค้า<input name="customer" required maxlength="180" value="${escapeHtml(contract.customer)}" placeholder="ชื่อ–นามสกุล"></label>
      <label>เบอร์โทร<input name="phone" maxlength="30" inputmode="tel" value="${escapeHtml(phone)}" placeholder="08x xxx xxxx"></label>
      <div class="field-pair"><label>เลขแปลง<input name="plot" required maxlength="60" value="${escapeHtml(contract.plot)}" placeholder="เช่น A-01"></label><label>วันชำระ<input name="dueDay" required type="number" min="1" max="28" value="${contract.dueDay}"></label></div>
      <label>เดือนเริ่มผ่อน (พ.ศ.)${thaiStartMonthFields(startDate)}<small class="field-help">เมื่อบันทึก ระบบจะเริ่มสร้างงวดใหม่จากเดือนนี้</small></label>
      <div class="edit-financial-heading"><div><small>แก้ไขยอดเงิน</small><strong>ข้อมูลสัญญาและตารางงวด</strong></div><span>ยังไม่เคยรับค่างวด</span></div>
      <label>ราคาขาย<input name="totalPrice" required type="number" min="1" step="0.01" inputmode="decimal" value="${Number(contract.totalPrice)}"></label>
      <div class="field-pair"><label>เงินดาวน์<input name="downPayment" required type="number" min="0" step="0.01" inputmode="decimal" value="${Number(contract.downPayment)}"></label><label>ยอดต่องวด<input name="installmentAmount" required type="number" min="0.01" step="0.01" inputmode="decimal" value="${Number(contract.installmentAmount)}"></label></div>
      ${interestFields(contract)}
      <div class="edit-schedule-preview contract-plan-preview" data-contract-plan-preview>${planPreviewMarkup(calculateContractPlan({ totalPrice: contract.totalPrice, downPayment: contract.downPayment, installmentAmount: contract.installmentAmount, interestMode: contract.interestMode, interestRate: contract.interestRate, interestAmount: contract.interestAmount }))}</div>
      <p class="edit-contract-note warning"><span>!</span> เมื่อบันทึก ระบบจะสร้างตารางงวดใหม่ทั้งหมดตามยอดและเดือนเริ่มผ่อนที่แก้ไข สถานะข้ามงวดเดิมของแปลงนี้จะเริ่มใหม่ด้วย</p>
      <p class="contract-form-error" data-contract-form-error role="alert" hidden></p>
      <button class="button button-gold" type="submit">บันทึกและสร้างตารางงวดใหม่</button>
    </form>`);
}

function updateContractPlanPreview(form) {
  const preview = form?.querySelector("[data-contract-plan-preview]");
  if (!preview) return;
  preview.innerHTML = planPreviewMarkup(contractPlanFromForm(form));
}

function showPaymentModal(number) {
  const contract = selectedContract();
  const installments = state.installments[contract.id] || buildInstallments(contract);
  const installment = installments.find((item) => item.no === Number(number)) || installments[0];
  if (!installment) return;
  if (installment.status === "waived") {
    showWaivedInstallmentModal(installment.no);
    return;
  }
  if (installment.status === "paid") {
    const prepayments = (state.prepayments[contract.id] || []).filter((item) => Number(item.installmentNo) === Number(installment.no));
    const remainingPrincipal = Math.max(0, Number(contract.totalPrice) - Number(contract.downPayment) - Number(contract.paidPrincipal ?? contract.paidTotal ?? 0));
    const slipLink = installment.slipUrl
      ? `<a class="receipt-slip-link" href="${escapeHtml(installment.slipUrl)}" target="_blank" rel="noopener">▣ เปิดดูสลิปที่แนบไว้</a>`
      : `<small class="receipt-no-slip">ยังไม่พบรูปสลิปของงวดนี้</small>`;
    const extraHistory = prepayments.length ? `<div class="prepayment-history"><header><span>ยอดโปะเงินต้นในงวดนี้</span><strong>฿${baht(prepayments.reduce((sum, item) => sum + item.amount, 0))}</strong></header>${prepayments.map((item) => `<article><div><small>${fullDate(item.paidAt)}</small><strong>฿${baht(item.amount)}</strong></div><div>${item.slipUrl ? `<a href="${escapeHtml(item.slipUrl)}" target="_blank" rel="noopener">เปิดสลิป</a>` : ""}<button data-action="replace-prepayment-slip" data-payment-id="${item.id}">เปลี่ยนรูป</button></div></article>`).join("")}</div>` : "";
    showModal(`หลักฐานงวดที่ ${installment.no}`, contract.customer, `<div class="paid-receipt"><span>✓</span><p>ชำระครบแล้ว</p><strong>฿${baht(installment.amount)}</strong><small>รับชำระวันที่ ${fullDate(installment.paidAt || installment.dueDate)}</small>${slipLink}<button class="receipt-replace-button" type="button" data-action="replace-slip" data-number="${installment.no}">↻ เปลี่ยนสลิปเป็นรูปใหม่</button></div>${extraHistory}<button class="button button-prepayment" data-action="principal-prepayment" ${remainingPrincipal <= 0 ? "disabled" : ""}>＋ บันทึกยอดโปะตามวันที่รับจริง</button><button class="button button-dark" data-action="close-modal">ปิด</button>`);
    return;
  }
  if (!isActionableInstallment(installment, installments)) {
    showToast("กรุณาชำระงวดก่อนหน้าตามลำดับ ระบบจะเปิดงวดนี้ให้อัตโนมัติ");
    return;
  }
  const historical = isHistoricalPayableInstallment(installment);
  const advance = new Date(installment.dueDate) > new Date();
  const todayValue = dateInputValue();
  const remainingAmount = Math.max(0, installment.amount - installment.paid);
  const remainingPrincipal = Math.max(0, installment.principal - installment.principalPaid);
  const remainingInterest = Math.max(0, installment.interest - installment.interestPaid);
  showModal(`${historical ? "บันทึกย้อนหลัง · " : ""}งวดที่ ${installment.no}`, contract.customer, `
    <div class="amount-panel ${historical ? "historical" : advance ? "advance" : ""}"><span>${historical ? "งวดที่ผ่านมาแล้ว" : advance ? "ชำระล่วงหน้า" : "ยอดที่ต้องชำระเต็มงวด"}</span><strong>฿${baht(remainingAmount)}</strong><small>เงินต้น ฿${baht(remainingPrincipal)} · ดอกเบี้ย ฿${baht(remainingInterest)} · ครบกำหนด ${fullDate(installment.dueDate)}</small></div>
    <form id="payment-form" class="form-stack modal-form" data-number="${installment.no}" data-historical="${historical ? "true" : "false"}">
      <label>จำนวนเงินที่ได้รับเต็มงวด<input name="amount" type="number" inputmode="decimal" value="${remainingAmount}" readonly required><small class="field-help">ระบบไม่รับชำระบางส่วน</small></label>
      <label>วันที่รับเงินจริง<input name="paid_date" type="date" value="${todayValue}" max="${todayValue}" required><small class="field-help">ค่าเริ่มต้นเป็นวันนี้ และเลือกวันที่ย้อนหลังได้ การแสดงปี พ.ศ. จะเป็นไปตามภาษาไทยของเครื่อง</small></label>
      <div class="installment-auto-date"><span>วันครบกำหนดตามสัญญา</span><strong>${fullDate(installment.dueDate)}</strong><small>วันครบกำหนดเดิมจะยังเก็บไว้ แม้ชำระล่วงหน้าหรือบันทึกย้อนหลัง</small></div>
      ${installment.slipUrl ? `<div class="existing-slip-note"><span>มีสลิปเดิมแล้ว</span><a href="${escapeHtml(installment.slipUrl)}" target="_blank" rel="noopener">เปิดดู</a><button type="button" data-action="replace-slip" data-number="${installment.no}">เปลี่ยนรูป</button></div>` : ""}
      <label class="slip-uploader"><input id="slip-file" data-slip-input name="slip" type="file" accept="image/*,.heic,.heif,.pdf,application/pdf" required><span>↑</span><strong data-slip-name>เลือกรูปหรือไฟล์สลิป</strong><small>เลือกจากคลังภาพหรือไฟล์ในเครื่อง · ไม่เกิน 15 MB</small></label>
      ${historical ? `<p class="historical-calc-note">ระบบจะคำนวณเงินเข้างวดและเดือนย้อนหลังนี้ให้อัตโนมัติ</p>` : ""}
      <button class="button button-gold" type="submit">${historical ? "ยืนยันและบันทึกย้อนหลัง" : advance ? "ยืนยันการชำระล่วงหน้า" : "ยืนยันการชำระเต็มงวด"}</button>
    </form>`);
}

function showReplaceSlipModal(number) {
  const contract = selectedContract();
  const installments = state.installments[contract.id] || buildInstallments(contract);
  const installment = installments.find((item) => item.no === Number(number));
  if (!installment?.id) return;
  showModal(`เปลี่ยนสลิป · งวดที่ ${installment.no}`, contract.customer, `
    <p class="replace-slip-note">รูปใหม่จะใช้แทนรูปเดิม แต่ยอดเงินและสถานะการชำระจะคงเดิม</p>
    <form id="replace-slip-form" class="form-stack modal-form" data-number="${installment.no}">
      <label class="slip-uploader"><input id="replace-slip-file" data-slip-input name="slip" type="file" accept="image/*,.heic,.heif,.pdf,application/pdf" required><span>↻</span><strong data-slip-name>เลือกรูปใหม่จากเครื่อง</strong><small>รองรับ JPG, PNG, WebP, HEIC, HEIF, GIF และ PDF</small></label>
      <button class="button button-gold" type="submit">บันทึกรูปใหม่แทนสลิปเดิม</button>
    </form>`);
}

function showPrincipalPrepaymentModal() {
  const contract = selectedContract();
  const installments = contract ? (state.installments[contract.id] || buildInstallments(contract)) : [];
  if (!contract || !installments.length) {
    showToast("ไม่พบตารางงวดสำหรับบันทึกยอดโปะ");
    return;
  }
  const todayValue = dateInputValue();
  const installment = installmentForPaymentDate(installments, todayValue);
  const financed = Number(contract.totalPrice) - Number(contract.downPayment);
  const remainingPrincipal = Math.max(0, financed - Number(contract.paidPrincipal ?? contract.paidTotal ?? 0));
  if (remainingPrincipal <= 0) {
    showToast("สัญญานี้ไม่มีเงินต้นคงเหลือสำหรับโปะ");
    return;
  }
  const monthlyPrincipal = Math.max(0, Number(contract.installmentAmount) - Number(contract.interestAmount || 0));
  const preview = calculatePrepaymentPlan({ remainingPrincipal, extraPrincipal: 0, monthlyPrincipal, monthlyInterest: contract.interestAmount });
  showModal("โปะเงินต้นเพิ่มเติม", contract.customer, `
    <div class="prepayment-rule-card"><span>↘</span><div><strong>เงินโปะหักเงินต้น 100%</strong><small>ค่างวด ฿${baht(contract.installmentAmount)} และดอกเบี้ย ฿${baht(contract.interestAmount)} ต่องวดยังคงเดิม จำนวนงวดในอนาคตจะลดลง</small></div></div>
    <form id="principal-prepayment-form" class="form-stack modal-form" data-contract-id="${contract.id}" data-remaining="${remainingPrincipal}" data-monthly-principal="${monthlyPrincipal}" data-monthly-interest="${Number(contract.interestAmount || 0)}">
      <label>ยอดโปะเงินต้นเพิ่มเติม<input name="amount" type="number" min="0.01" max="${remainingPrincipal}" step="0.01" inputmode="decimal" placeholder="0 บาท" required><small class="field-help">ไม่รวมค่างวดปกติ และต้องไม่เกินเงินต้นคงเหลือ ฿${baht(remainingPrincipal)}</small></label>
      <div class="prepayment-preview" data-prepayment-preview><span><small>เงินต้นหลังโปะ</small><strong>฿${baht(preview.remainingPrincipal)}</strong></span><span><small>งวดที่จะลดลง</small><strong>${preview.installmentsReduced} งวด</strong></span><span><small>งวดสุดท้าย</small><strong>฿${baht(preview.finalInstallment)}</strong></span></div>
      <label>วันที่รับเงินจริง<input name="paid_date" type="date" value="${todayValue}" max="${todayValue}" required><small class="field-help">ค่าเริ่มต้นเป็นวันนี้ ระบบจะบันทึกยอดและสลิปไว้ใต้รายการงวดของเดือนที่เลือก</small></label>
      <div class="prepayment-month-target" data-prepayment-month-target><span>สลิปยอดโปะจะแสดงใต้</span><strong>งวดที่ ${installment.no} · ${new Intl.DateTimeFormat("th-TH", { month: "long", year: "numeric" }).format(installment.dueDate)}</strong><small>วันที่โปะ ${fullDate(new Date(`${todayValue}T00:00:00`))}</small></div>
      <label class="slip-uploader"><input id="prepayment-slip-file" data-slip-input name="slip" type="file" accept="image/*,.heic,.heif,.pdf,application/pdf" required><span>↑</span><strong data-slip-name>แนบสลิปยอดโปะแยกอีกใบ</strong><small>เลือกจากคลังภาพหรือไฟล์ในเครื่อง · ไม่เกิน 15 MB</small></label>
      <button class="button button-gold" type="submit">ยืนยันโปะเงินต้นและคำนวณงวดใหม่</button>
    </form>`);
}

function updatePrepaymentPreview(form) {
  const preview = form?.querySelector("[data-prepayment-preview]");
  if (!preview) return;
  const plan = calculatePrepaymentPlan({
    remainingPrincipal: Number(form.dataset.remaining || 0),
    extraPrincipal: Number(form.elements.amount?.value || 0),
    monthlyPrincipal: Number(form.dataset.monthlyPrincipal || 0),
    monthlyInterest: Number(form.dataset.monthlyInterest || 0),
  });
  preview.innerHTML = `<span><small>เงินต้นหลังโปะ</small><strong>฿${baht(plan.remainingPrincipal)}</strong></span><span><small>งวดที่จะลดลง</small><strong>${plan.installmentsReduced} งวด</strong></span><span><small>งวดสุดท้าย</small><strong>฿${baht(plan.finalInstallment)}</strong></span>`;
  const monthTarget = form.querySelector("[data-prepayment-month-target]");
  if (monthTarget) {
    const contract = selectedContract();
    const installment = installmentForPaymentDate(state.installments[contract?.id] || buildInstallments(contract), form.elements.paid_date?.value);
    const paidDate = new Date(`${String(form.elements.paid_date?.value || dateInputValue())}T00:00:00`);
    monthTarget.innerHTML = installment
      ? `<span>สลิปยอดโปะจะแสดงใต้</span><strong>งวดที่ ${installment.no} · ${new Intl.DateTimeFormat("th-TH", { month: "long", year: "numeric" }).format(installment.dueDate)}</strong><small>วันที่โปะ ${fullDate(paidDate)}</small>`
      : `<span>ไม่พบงวดของเดือนที่เลือก</span>`;
  }
}

function showReplacePrepaymentSlipModal(paymentId) {
  const contract = selectedContract();
  const payment = (state.prepayments[contract?.id] || []).find((item) => item.id === Number(paymentId));
  if (!payment) return;
  showModal("เปลี่ยนสลิปยอดโปะ", `${contract.customer} · ฿${baht(payment.amount)}`, `
    <p class="replace-slip-note">รูปใหม่จะใช้แทนสลิปยอดโปะเดิม โดยยอดเงินและจำนวนงวดไม่เปลี่ยน</p>
    <form id="replace-prepayment-slip-form" class="form-stack modal-form" data-payment-id="${payment.id}">
      <label class="slip-uploader"><input data-slip-input name="slip" type="file" accept="image/*,.heic,.heif,.pdf,application/pdf" required><span>↻</span><strong data-slip-name>เลือกรูปใหม่จากเครื่อง</strong><small>รองรับรูปภาพและ PDF · ไม่เกิน 15 MB</small></label>
      <button class="button button-gold" type="submit">บันทึกรูปใหม่แทนสลิปยอดโปะ</button>
    </form>`);
}

function showSlipTestModal() {
  showModal("ตรวจสอบระบบแนบสลิป", "ทดสอบพื้นที่จัดเก็บโดยไม่บันทึกข้อมูลลูกค้า", `
    <form id="slip-test-form" class="form-stack modal-form">
      <label class="slip-uploader"><input id="test-slip-file" data-slip-input name="slip" type="file" accept="image/*,.heic,.heif,.pdf,application/pdf" required><span>✓</span><strong data-slip-name>เลือกไฟล์สำหรับทดสอบ</strong><small>ระบบจะทดสอบแล้วลบไฟล์นี้ทันที</small></label>
      <button class="button button-gold" type="submit">เริ่มทดสอบการบันทึกสลิป</button>
    </form>`);
}

function showHistoricalPaymentsModal() {
  const contract = selectedContract();
  if (!contract) return;
  const installments = state.installments[contract.id] || buildInstallments(contract);
  const historical = installments.filter(isHistoricalPayableInstallment).sort((left, right) => new Date(left.dueDate) - new Date(right.dueDate));
  const firstOpen = firstOpenInstallment(installments);
  if (!historical.length) {
    showModal("ชำระงวดย้อนหลัง", contract.customer, `<div class="historical-empty"><span>✓</span><strong>ไม่มีงวดค้างย้อนหลัง</strong><small>งวดที่ผ่านมาบันทึกครบแล้ว</small></div><button class="button button-dark" data-action="close-modal">ปิด</button>`);
    return;
  }
  showModal("เลือกงวดย้อนหลัง", contract.customer, `
    <p class="historical-modal-note">เลือกงวด แล้วเลือกว่าจะบันทึกการชำระพร้อมสลิป หรืออนุญาตข้ามงวดนั้น</p>
    <div class="historical-installment-list">${historical.map((item) => { const actionable = Number(item.no) === Number(firstOpen?.no); return `<div class="historical-installment-item ${actionable ? "" : "sequence-locked"}"><button class="historical-pay-action" data-action="payment" data-number="${item.no}" ${actionable ? "" : "disabled"}><span>${item.no}</span><div><strong>งวดที่ ${item.no}</strong><small>ครบกำหนด ${shortDate(item.dueDate)}${actionable ? "" : " · รอชำระงวดก่อนหน้า"}</small></div><b>เต็มงวด ฿${baht(Math.max(0, item.amount - item.paid))}</b><em>›</em></button><button class="historical-skip-action" data-action="skip-installment" data-number="${item.no}" ${actionable ? "" : "disabled"}><span>↷</span> อนุญาตข้ามงวดย้อนหลัง</button></div>`; }).join("")}</div>`);
}

function showSkipInstallmentModal(number) {
  const contract = selectedContract();
  const installments = state.installments[contract?.id] || [];
  const installment = installments.find((item) => item.no === Number(number));
  if (!contract || !installment) return;
  const lastDueDate = installments.at(-1)?.dueDate || installment.dueDate;
  const appendedDueDate = new Date(lastDueDate);
  appendedDueDate.setMonth(appendedDueDate.getMonth() + 1);
  showModal("อนุญาตข้ามงวด", contract.customer, `
    <div class="skip-confirmation">
      <span>↷</span><div><strong>ข้ามงวดที่ ${installment.no} โดยไม่ถือว่าค้าง?</strong><p>งวดวันที่ ${fullDate(installment.dueDate)} จะเปลี่ยนเป็น “อนุญาตข้ามงวด” และเพิ่มงวดที่ ${contract.totalInstallments + 1} ต่อท้ายประมาณวันที่ ${fullDate(appendedDueDate)}</p></div>
    </div>
    <div class="skip-summary"><span><small>ยอดเดือนที่ย้ายไปท้ายสัญญา</small><strong>฿${baht(installment.amount)}</strong></span><span><small>ยอดเงินรวม</small><strong>ไม่เปลี่ยน</strong></span><span><small>จำนวนงวดใหม่</small><strong>${contract.totalInstallments + 1}</strong></span></div>
    <div class="delete-confirm-actions"><button class="button button-dark" data-action="close-modal">ยกเลิก</button><button class="button button-skip-confirm" data-action="confirm-skip-installment" data-number="${installment.no}">ยืนยันอนุญาตข้าม</button></div>`);
}

function showWaivedInstallmentModal(number) {
  const contract = selectedContract();
  const installments = state.installments[contract?.id] || [];
  const installment = installments.find((item) => item.no === Number(number));
  if (!contract || !installment) return;
  showModal(`งวดที่ ${installment.no} · อนุญาตข้าม`, contract.customer, `
    <div class="waived-installment-detail"><span>↷</span><strong>งวดนี้ได้รับอนุญาตให้ข้าม</strong><p>${fullDate(installment.dueDate)} · ไม่มียอดค้างชำระ และระบบเพิ่มงวดชำระไว้ท้ายสัญญาแล้ว</p></div>
    <div class="delete-confirm-actions"><button class="button button-dark" data-action="close-modal">ปิด</button><button class="button undo-skip-button" data-action="undo-skip-installment" data-number="${installment.no}">ยกเลิกการข้ามงวด</button></div>`);
}

function showWorkspaceSwitchModal() {
  const current = activeWorkspace();
  showModal("เปลี่ยนพื้นที่ทำงาน", `ขณะนี้กำลังใช้ข้อมูลของ ${current?.name || ""}`, `
    <div class="workspace-switch-list">
      ${state.workspaces.map((workspace) => {
        const active = Number(workspace.id) === Number(state.activeWorkspaceId);
        const initials = workspace.initials || workspace.name.split(" ").map((part) => part[0]).join("");
        return `<button class="workspace-switch-item ${active ? "active" : ""}" data-action="select-workspace" data-workspace-id="${workspace.id}" ${active ? "disabled" : ""}>
          <span>${escapeHtml(initials)}</span><div><small>พื้นที่ข้อมูล</small><strong>${escapeHtml(workspace.name)}</strong></div><b>${active ? "กำลังใช้งาน" : "เลือก ›"}</b>
        </button>`;
      }).join("")}
    </div>
    <p class="workspace-switch-note">เมื่อเปลี่ยนแล้ว แอปจะจำพื้นที่นี้ไว้และเปิดเข้าพื้นที่นี้อัตโนมัติในครั้งต่อไป</p>`);
}

function showSettingsModal() {
  showModal("ตั้งค่า", "ข้อมูลแอปและพื้นที่ทำงาน", `<div class="settings-list"><button data-action="switch-workspace"><span>GL</span><div><strong>${escapeHtml(activeWorkspace()?.name || "เลือกพื้นที่ทำงาน")}</strong><small>เปลี่ยนพื้นที่ข้อมูลผู้ใช้งาน</small></div><b>›</b></button><button data-action="test-slip-storage"><span>✓</span><div><strong>ตรวจสอบระบบแนบสลิป</strong><small>ทดสอบว่าโฮสติ้งรับและจัดเก็บไฟล์ได้</small></div><b>›</b></button><button><span>▣</span><div><strong>บัญชีรับเงิน</strong><small>เพิ่มชื่อธนาคารและเลขบัญชี</small></div><b>›</b></button><button><span>↻</span><div><strong>สำรองข้อมูล</strong><small>สำรอง MySQL และไฟล์สลิป</small></div><b>›</b></button><button><span>?</span><div><strong>คู่มือการใช้งาน</strong><small>วิธีเพิ่มสัญญาและบันทึกเงิน</small></div><b>›</b></button></div><p class="version">GOLDEN LANDPAY เวอร์ชัน 5.4</p>`);
}

function showDeleteCustomerModal() {
  const contract = selectedContract();
  if (!contract) return;
  showModal("ลบลูกค้าและแปลง", contract.customer, `
    <form id="delete-contract-form" class="delete-contract-form" data-id="${contract.id}">
    <div class="delete-warning">
      <span>!</span>
      <div><strong>เลือกวิธีนำแปลง ${escapeHtml(contract.plot)} ออกจากระบบ</strong><p>ตรวจสอบให้ถูกต้องก่อนยืนยัน โดยเฉพาะการลบถาวรซึ่งจะทำให้ยอดย้อนหลังเปลี่ยนแปลง</p></div>
    </div>
    <div class="delete-mode-list">
      <label class="delete-mode-card recommended">
        <input type="radio" name="delete_mode" value="archive" checked>
        <span class="delete-mode-icon">✓</span>
        <span><strong>นำแปลงออก แต่เก็บประวัติ</strong><small>เก็บยอดเงิน วันที่ชำระ และสลิปเดิมไว้ ยอดย้อนหลังไม่เปลี่ยน</small><b>แนะนำ</b></span>
      </label>
      <label class="delete-mode-card permanent">
        <input type="radio" name="delete_mode" value="purge">
        <span class="delete-mode-icon">⌫</span>
        <span><strong>ลบถาวรพร้อมประวัติทั้งหมด</strong><small>ลบงวด การชำระ และสลิปทั้งหมด ยอดย้อนหลังจะคำนวณใหม่</small><b>ใช้เมื่อบันทึกข้อมูลผิดเท่านั้น</b></span>
      </label>
    </div>
    <label class="delete-password-field"><span>กรอกรหัสเพื่อยืนยันการลบ</span><div><input name="password" type="password" inputmode="numeric" autocomplete="off" maxlength="5" pattern="[0-9]{5}" placeholder="รหัส 5 หลัก" required><button type="button" data-action="toggle-delete-password">แสดง</button></div><small>ระบบจะขอรหัสใหม่ทุกครั้งก่อนลบ</small></label>
    <div class="delete-confirm-actions">
      <button class="button button-dark" data-action="close-modal">ยกเลิก</button>
      <button type="submit" class="button button-danger">ยืนยันการลบ</button>
    </div>
    </form>`);
}

function showModal(title, subtitle, content) {
  overlayRoot.innerHTML = `<div class="modal-backdrop"><section class="bottom-sheet" role="dialog" aria-modal="true" aria-label="${escapeHtml(title)}"><div class="sheet-handle"></div><header><div><h2>${escapeHtml(title)}</h2><p>${escapeHtml(subtitle)}</p></div><button data-action="close-modal" aria-label="ปิด">×</button></header>${content}</section></div>`;
}

function closeModal() {
  overlayRoot.innerHTML = "";
}

function showToast(message) {
  toast.textContent = message;
  toast.classList.add("show");
  clearTimeout(showToast.timer);
  showToast.timer = setTimeout(() => toast.classList.remove("show"), 3200);
}

async function login(form) {
  const password = String(new FormData(form).get("password") || "").trim();
  if (isDemoHost()) {
    if (password !== "14052") throw new Error("รหัสผ่านไม่ถูกต้อง");
    localStorage.setItem(DEVICE_TOKEN_KEY, "trusted-demo");
    state.workspaces = structuredClone(demoWorkspaces);
    state.activeWorkspaceId = null;
  } else {
    const response = await fetch("/backend/api.php?action=initialize", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ password }) });
    const payload = await response.json();
    if (!response.ok || !payload.token) throw new Error(payload.error || "เข้าใช้งานไม่สำเร็จ");
    localStorage.setItem(DEVICE_TOKEN_KEY, payload.token);
    state.workspaces = payload.workspaces || [];
    state.activeWorkspaceId = payload.activeWorkspaceId || null;
  }
  state.authenticated = true;
  render();
}

async function loadSession() {
  if (isDemoHost()) {
    state.workspaces = structuredClone(demoWorkspaces);
    state.activeWorkspaceId = Number(localStorage.getItem(WORKSPACE_KEY)) || null;
    if (state.activeWorkspaceId) loadDemoWorkspace(state.activeWorkspaceId);
    return;
  }
  const response = await apiRequest("session");
  if (!response.ok) {
    if (response.status === 401) {
      localStorage.removeItem(DEVICE_TOKEN_KEY);
      state.authenticated = false;
    }
    throw new Error("กรุณาเข้าใช้งานใหม่");
  }
  const payload = await response.json();
  state.workspaces = payload.workspaces || [];
  state.activeWorkspaceId = payload.activeWorkspaceId || null;
  if (state.activeWorkspaceId) localStorage.setItem(WORKSPACE_KEY, String(state.activeWorkspaceId));
  else localStorage.removeItem(WORKSPACE_KEY);
}

function loadDemoWorkspace(workspaceId) {
  state.contracts = structuredClone(demoContracts.filter((item) => Number(item.workspaceId) === Number(workspaceId)));
  state.monthlyIncome = Number(workspaceId) === 1 ? [18000, 24500, 22000, 31000, 27000, 14000] : [21000, 28000, 25000, 34000, 31000, 0];
  state.monthlyInterest = [0, 0, 0, 0, 0, 0];
  state.interestStats = { receivedThisMonth: 0, principalThisMonth: Number(state.monthlyIncome.at(-1) || 0), total: 0, receivedLifetime: 0, outstanding: 0 };
  state.selectedId = state.contracts[0]?.id || null;
}

async function selectWorkspace(workspaceId) {
  const selected = state.workspaces.find((item) => Number(item.id) === Number(workspaceId));
  if (!selected) throw new Error("ไม่พบพื้นที่ทำงานที่เลือก");
  if (!isDemoHost()) {
    const response = await apiRequest("workspace", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ workspace_id: Number(workspaceId) }),
    });
    const payload = await response.json();
    if (!response.ok) throw new Error(payload.error || "เปลี่ยนพื้นที่ทำงานไม่สำเร็จ");
  }
  state.activeWorkspaceId = Number(workspaceId);
  state.installments = {};
  state.prepayments = {};
  state.search = "";
  state.filter = "all";
  state.screen = "dashboard";
  localStorage.setItem(WORKSPACE_KEY, String(workspaceId));
  if (isDemoHost()) loadDemoWorkspace(workspaceId);
  else await Promise.all([loadContracts(), loadDashboardStats()]);
  closeModal();
  render();
  showToast(`กำลังทำงานในพื้นที่ ${selected.name}`);
}

async function loadContracts() {
  const response = await apiRequest("contracts");
  if (!response.ok) throw new Error("เชื่อมต่อฐานข้อมูลไม่สำเร็จ");
  state.contracts = (await response.json()).map((contract) => ({
    ...contract,
    interestMode: contract.interestMode || "none",
    interestRate: Number(contract.interestRate || 0),
    interestAmount: Number(contract.interestAmount || 0),
    paidPrincipal: Number(contract.paidPrincipal ?? contract.paidTotal ?? 0),
    paidInterest: Number(contract.paidInterest || 0),
    totalInterest: Number(contract.totalInterest || 0),
    remainingInterest: Number(contract.remainingInterest || 0),
  }));
  if (!state.selectedId && state.contracts[0]) state.selectedId = state.contracts[0].id;
}

async function loadDashboardStats() {
  if (isDemoHost()) return;
  const response = await apiRequest("dashboard");
  if (!response.ok) throw new Error("โหลดกราฟเงินรับรายเดือนไม่สำเร็จ");
  const payload = await response.json();
  state.monthlyIncome = (payload.monthlyIncome || []).map(Number);
  state.monthlyInterest = (payload.monthlyInterest || []).map(Number);
  state.interestStats = {
    receivedThisMonth: Number(payload.interestReceivedThisMonth || 0),
    principalThisMonth: Number(payload.principalReceivedThisMonth || 0),
    total: Number(payload.totalInterest || 0),
    receivedLifetime: Number(payload.interestReceivedLifetime || 0),
    outstanding: Number(payload.interestOutstanding || 0),
  };
}

async function loadInstallments(contractId) {
  if (isDemoHost()) return;
  const response = await apiRequest(`contract&id=${contractId}`);
  if (!response.ok) return;
  const payload = await response.json();
  state.installments[contractId] = payload.installments.map((item) => ({ id: Number(item.id), no: Number(item.installment_no), dueDate: new Date(`${item.due_date}T00:00:00`), paidAt: item.paid_at ? new Date(String(item.paid_at).replace(" ", "T")) : null, amount: Number(item.amount_due), paid: Number(item.amount_paid), principal: Number(item.principal_due), interest: Number(item.interest_due), principalPaid: Number(item.principal_paid), interestPaid: Number(item.interest_paid), status: item.status, paymentId: Number(item.payment_id || 0), slipPath: String(item.slip_path || ""), slipName: String(item.slip_original_name || ""), slipUrl: item.slip_path ? `/backend/${String(item.slip_path).replace(/^\/+/, "")}` : "" }));
  state.prepayments[contractId] = (payload.prepayments || []).map((item) => ({ id: Number(item.id), installmentId: Number(item.installment_id), installmentNo: Number(item.installment_no), amount: Number(item.amount), principal: Number(item.principal_amount), paidAt: new Date(String(item.paid_at).replace(" ", "T")), slipPath: String(item.slip_path || ""), slipName: String(item.slip_original_name || ""), slipUrl: item.slip_path ? `/backend/${String(item.slip_path).replace(/^\/+/, "")}` : "" }));
  render();
}

function apiRequest(action, init = {}) {
  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${localStorage.getItem(DEVICE_TOKEN_KEY) || ""}`);
  return fetch(`/backend/api.php?action=${action}`, { ...init, headers });
}

function newRequestId() {
  return globalThis.crypto?.randomUUID?.() || `glp-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function wait(milliseconds) {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

async function readApiPayload(response) {
  try {
    return await response.json();
  } catch {
    return { error: response.status === 413 ? "ไฟล์ใหญ่เกินค่าที่โฮสติ้งอนุญาต" : "เซิร์ฟเวอร์ตอบกลับไม่สมบูรณ์ กรุณาลองใหม่" };
  }
}

function validateSlipFile(file) {
  if (!file || !file.name) throw new Error("กรุณาเลือกรูปหรือไฟล์สลิปก่อนบันทึก");
  if (file.size > 15 * 1024 * 1024) throw new Error("ไฟล์สลิปต้องมีขนาดไม่เกิน 15 MB");
}

function setSubmitBusy(form, busy, textValue = "กำลังบันทึกสลิป...") {
  const button = form.querySelector('button[type="submit"]');
  if (!button) return;
  if (busy) {
    button.dataset.originalText = button.textContent;
    button.textContent = textValue;
    button.disabled = true;
  } else {
    button.textContent = button.dataset.originalText || button.textContent;
    button.disabled = false;
  }
}

document.addEventListener("click", async (event) => {
  const target = event.target.closest("[data-action]");
  if (!target) {
    if (event.target.classList.contains("modal-backdrop")) closeModal();
    return;
  }
  const action = target.dataset.action;
  if (action === "toggle-password") {
    const input = document.querySelector("#login-form input[name=password]");
    input.type = input.type === "password" ? "text" : "password";
    target.textContent = input.type === "password" ? "แสดง" : "ซ่อน";
  } else if (action === "select-workspace") {
    await selectWorkspace(Number(target.dataset.workspaceId));
  } else if (action === "switch-workspace") {
    showWorkspaceSwitchModal();
  } else if (action === "home") {
    state.screen = "dashboard"; render(); window.scrollTo({ top: 0, behavior: "smooth" });
  } else if (action === "offer") {
    state.screen = "offer"; render(); window.scrollTo({ top: 0, behavior: "smooth" });
  } else if (action === "customers" || action === "back-customers") {
    state.screen = "customers"; render(); window.scrollTo({ top: 0, behavior: "smooth" });
  } else if (action === "open-contract") {
    state.selectedId = Number(target.dataset.id); state.installmentFilter = "all"; state.installmentPage = 1; state.screen = "contract"; render(); window.scrollTo({ top: 0, behavior: "smooth" }); await loadInstallments(state.selectedId);
  } else if (action === "quick-payment") {
    state.selectedId = Number(target.dataset.id);
    state.installmentFilter = "all";
    state.installmentPage = 1;
    state.screen = "contract";
    render();
    await loadInstallments(state.selectedId);
    const contract = selectedContract();
    const nextInstallment = contract ? (state.installments[contract.id] || []).find((item) => !["paid", "waived"].includes(item.status)) : null;
    if (nextInstallment) showPaymentModal(nextInstallment.no);
    else showToast("สัญญานี้ชำระครบทุกงวดแล้ว");
  } else if (action === "add") showAddModal();
  else if (action === "reset-offer") {
    state.offerDraft = { totalPrice: "", downPayment: "", installmentAmount: "", interestMode: "none", interestRate: "", interestAmount: "" };
    render();
  }
  else if (action === "edit-contract") showEditContractModal();
  else if (action === "settings") showSettingsModal();
  else if (action === "test-slip-storage") showSlipTestModal();
  else if (action === "historical-payments") showHistoricalPaymentsModal();
  else if (action === "replace-slip") showReplaceSlipModal(target.dataset.number);
  else if (action === "principal-prepayment") showPrincipalPrepaymentModal(target.dataset.number);
  else if (action === "replace-prepayment-slip") showReplacePrepaymentSlipModal(target.dataset.paymentId);
  else if (action === "skip-installment") {
    try { showSkipInstallmentModal(target.dataset.number); }
    catch (error) { showToast(error.message || "เปิดหน้าต่างข้ามงวดไม่สำเร็จ"); }
  }
  else if (action === "confirm-skip-installment") await skipInstallment(Number(target.dataset.number));
  else if (action === "waived-installment") showWaivedInstallmentModal(target.dataset.number);
  else if (action === "undo-skip-installment") await undoSkipInstallment(Number(target.dataset.number));
  else if (action === "installment-filter") { state.installmentFilter = target.dataset.filter || "all"; state.installmentPage = 1; render(); }
  else if (action === "installment-page") { state.installmentPage = Math.max(1, Number(target.dataset.page) || 1); render(); document.querySelector(".installment-history-section")?.scrollIntoView({ behavior: "smooth", block: "start" }); }
  else if (action === "delete-customer") showDeleteCustomerModal();
  else if (action === "toggle-delete-password") {
    const input = document.querySelector('#delete-contract-form input[name="password"]');
    if (input) {
      input.type = input.type === "password" ? "text" : "password";
      target.textContent = input.type === "password" ? "แสดง" : "ซ่อน";
    }
  }
  else if (action === "close-modal") closeModal();
  else if (action === "payment") showPaymentModal(target.dataset.number);
  else if (action === "clear-search") { state.search = ""; render(); }
  else if (action === "filter") { state.filter = target.dataset.filter; render(); }
  else if (action === "install") await installApp();
});

document.addEventListener("input", (event) => {
  if (event.target.id === "customer-search") {
    state.search = event.target.value;
    const cursor = event.target.selectionStart;
    render();
    const input = document.querySelector("#customer-search");
    input?.focus(); input?.setSelectionRange(cursor, cursor);
  }
  const contractForm = event.target.closest("#edit-contract-form, #add-contract-form");
  if (contractForm && ["totalPrice", "downPayment", "installmentAmount", "interestRate", "interestAmount"].includes(event.target.name)) updateContractPlanPreview(contractForm);
  const offerForm = event.target.closest("#offer-calculator-form");
  if (offerForm && ["totalPrice", "downPayment", "installmentAmount", "interestRate", "interestAmount"].includes(event.target.name)) updateOfferCalculator(offerForm);
  const prepaymentForm = event.target.closest("#principal-prepayment-form");
  if (prepaymentForm && ["amount", "paid_date"].includes(event.target.name)) updatePrepaymentPreview(prepaymentForm);
});

document.addEventListener("change", (event) => {
  if (event.target.matches("[data-slip-input]")) {
    const file = event.target.files?.[0];
    const label = event.target.closest(".slip-uploader")?.querySelector("[data-slip-name]");
    if (file && label) label.textContent = `${file.name} · ${(file.size / 1024 / 1024).toFixed(1)} MB`;
    event.target.closest(".slip-uploader")?.classList.toggle("has-file", Boolean(file));
  }
  const startMonthPicker = event.target.closest("[data-start-month]");
  if (startMonthPicker) syncThaiStartMonth(startMonthPicker);
  const contractForm = event.target.closest("#edit-contract-form, #add-contract-form");
  if (contractForm && event.target.name === "interestMode") syncInterestFields(contractForm);
  const offerForm = event.target.closest("#offer-calculator-form");
  if (offerForm && event.target.name === "interestMode") syncInterestFields(offerForm);
});

document.addEventListener("submit", async (event) => {
  event.preventDefault();
  try {
    if (event.target.id === "login-form") await login(event.target);
    if (event.target.id === "add-contract-form") await addContract(event.target);
    if (event.target.id === "edit-contract-form") await updateContract(event.target);
    if (event.target.id === "payment-form") await recordPayment(event.target);
    if (event.target.id === "replace-slip-form") await replaceSlip(event.target);
    if (event.target.id === "principal-prepayment-form") await recordPrincipalPrepayment(event.target);
    if (event.target.id === "replace-prepayment-slip-form") await replacePrepaymentSlip(event.target);
    if (event.target.id === "slip-test-form") await testSlipStorage(event.target);
    if (event.target.id === "delete-contract-form") await deleteCustomer(event.target);
  } catch (error) {
    showToast(error.message || "เกิดข้อผิดพลาด กรุณาลองใหม่");
  }
});

async function addContract(form) {
  const { data, plan } = validateContractForm(form);
  const submitButton = form.querySelector('[type="submit"]');
  if (submitButton?.disabled) return;
  if (submitButton) {
    submitButton.disabled = true;
    submitButton.textContent = "กำลังบันทึกและสร้างตารางงวด…";
  }
  const contract = {
    id: Date.now(), workspaceId: Number(state.activeWorkspaceId), customer: data.customer, initials: data.customer.replaceAll(" ", "").slice(0, 2), phone: data.phone || "ยังไม่ระบุ", plot: data.plot,
    contractNo: `LD-${new Date().getFullYear() + 543}-${String(Date.now()).slice(-3)}`,
    totalPrice: Number(data.totalPrice), downPayment: Number(data.downPayment), installmentAmount: Number(data.installmentAmount), interestMode: String(data.interestMode || "none"), interestRate: Number(data.interestRate || 0), interestAmount: plan.monthlyInterest, totalInterest: plan.totalInterest, remainingInterest: plan.totalInterest, totalInstallments: plan.totalInstallments,
    paidInstallments: 0, paidTotal: 0, paidPrincipal: 0, paidInterest: 0, dueDay: Number(data.dueDay), startDate: String(data.start_date || ""), monthPaid: 0, status: "upcoming", tone: "emerald",
  };
  try {
    if (isDemoHost()) state.contracts.push(contract);
    else {
      const requestBody = JSON.stringify({ request_id: newRequestId(), customer: contract.customer, phone: contract.phone, plot: contract.plot, total_price: contract.totalPrice, down_payment: contract.downPayment, installment_amount: contract.installmentAmount, interest_mode: contract.interestMode, interest_rate: contract.interestRate, interest_amount: Number(data.interestAmount || 0), due_day: contract.dueDay, start_date: contract.startDate });
      let response;
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          response = await apiRequest("contracts", { method: "POST", headers: { "Content-Type": "application/json" }, body: requestBody });
          break;
        } catch (networkError) {
          if (attempt === 1) throw new Error("เชื่อมต่อเซิร์ฟเวอร์ไม่สำเร็จ กรุณาตรวจอินเทอร์เน็ตแล้วกดบันทึกอีกครั้ง");
          contractFormError(form, "การเชื่อมต่อสะดุด กำลังตรวจสอบและลองบันทึกให้อีกครั้ง…");
          await wait(1200);
        }
      }
      if (!response) throw new Error("เชื่อมต่อเซิร์ฟเวอร์ไม่สำเร็จ กรุณาลองใหม่");
      const payload = await readApiPayload(response);
      if (!response.ok) throw new Error(payload.error || "เพิ่มสัญญาไม่สำเร็จ");
      await loadContracts();
    }
    closeModal(); state.screen = "customers"; render(); showToast("เพิ่มลูกค้าและสัญญาใหม่แล้ว");
  } catch (error) {
    contractFormError(form, error.message || "เพิ่มสัญญาไม่สำเร็จ กรุณาลองใหม่");
    if (submitButton) {
      submitButton.disabled = false;
      submitButton.textContent = "บันทึกและสร้างตารางงวด";
    }
    throw error;
  }
}

async function updateContract(form) {
  const contractId = Number(form.dataset.id);
  const contract = state.contracts.find((item) => Number(item.id) === contractId);
  if (!contract) throw new Error("ไม่พบสัญญาที่ต้องการแก้ไข");
  const data = Object.fromEntries(new FormData(form));
  const customer = String(data.customer || "").trim();
  const phone = String(data.phone || "").trim();
  const plot = String(data.plot || "").trim();
  const dueDay = Number(data.dueDay);
  const totalPrice = Number(data.totalPrice);
  const downPayment = Number(data.downPayment);
  const installmentAmount = Number(data.installmentAmount);
  const interestMode = String(data.interestMode || "none");
  const interestRate = Number(data.interestRate || 0);
  const fixedInterestAmount = Number(data.interestAmount || 0);
  const plan = contractPlanFromForm(form);
  const startDate = String(data.start_date || "");
  if (!customer || !plot) throw new Error("กรุณากรอกชื่อลูกค้าและเลขแปลง");
  if (!Number.isInteger(dueDay) || dueDay < 1 || dueDay > 28) throw new Error("วันชำระต้องอยู่ระหว่างวันที่ 1–28");
  if (!Number.isFinite(totalPrice) || totalPrice <= 0) throw new Error("ราคาขายต้องมากกว่า 0 บาท");
  if (!Number.isFinite(downPayment) || downPayment < 0 || downPayment >= totalPrice) throw new Error("เงินดาวน์ต้องไม่น้อยกว่า 0 และน้อยกว่าราคาขาย");
  if (!Number.isFinite(installmentAmount) || installmentAmount <= 0) throw new Error("ยอดต่องวดต้องมากกว่า 0 บาท");
  if (plan.monthlyPrincipal <= 0) throw new Error("ยอดต่องวดต้องมากกว่าดอกเบี้ยต่องวด");
  if (!/^\d{4}-\d{2}-01$/.test(startDate)) throw new Error("กรุณาเลือกเดือนเริ่มผ่อน");
  if (Number(contract.paidTotal) > 0 || Number(contract.paidInstallments) > 0) throw new Error("แปลงนี้เคยรับเงินค่างวดแล้ว จึงไม่สามารถแก้ไขได้");

  setSubmitBusy(form, true, "กำลังบันทึกข้อมูล...");
  try {
    if (isDemoHost()) {
      Object.assign(contract, { customer, initials: customer.replaceAll(" ", "").slice(0, 2), phone: phone || "ยังไม่ระบุ", plot, dueDay, startDate, totalPrice, downPayment, installmentAmount, interestMode, interestRate, interestAmount: plan.monthlyInterest, totalInterest: plan.totalInterest, remainingInterest: plan.totalInterest, totalInstallments: plan.totalInstallments, paidInstallments: 0, skippedInstallments: 0, paidTotal: 0, paidPrincipal: 0, paidInterest: 0, monthPaid: 0, status: "upcoming" });
      state.installments[contract.id] = buildInstallments(contract);
    } else {
      const response = await apiRequest(`contract&id=${contract.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customer, phone, plot, due_day: dueDay, start_date: startDate, total_price: totalPrice, down_payment: downPayment, installment_amount: installmentAmount, interest_mode: interestMode, interest_rate: interestRate, interest_amount: fixedInterestAmount }),
      });
      const payload = await readApiPayload(response);
      if (!response.ok) throw new Error(payload.error || "แก้ไขข้อมูลไม่สำเร็จ");
      await loadContracts();
      await loadInstallments(contract.id);
    }
    closeModal();
    render();
    showToast("บันทึกข้อมูลและสร้างตารางงวดใหม่แล้ว");
  } finally {
    if (document.body.contains(form)) setSubmitBusy(form, false);
  }
}

async function deleteCustomer(form) {
  const contractId = Number(form.dataset.id);
  const contract = state.contracts.find((item) => Number(item.id) === Number(contractId));
  if (!contract) throw new Error("ไม่พบลูกค้าและแปลงที่ต้องการลบ");
  const data = Object.fromEntries(new FormData(form));
  const password = String(data.password || "").trim();
  const deleteMode = data.delete_mode === "purge" ? "purge" : "archive";
  if (!/^\d{5}$/.test(password)) throw new Error("กรุณากรอกรหัสยืนยัน 5 หลัก");
  setSubmitBusy(form, true, deleteMode === "purge" ? "กำลังลบถาวร..." : "กำลังนำแปลงออก...");
  try {
    if (!isDemoHost()) {
      const response = await apiRequest(`contract&id=${contract.id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password, delete_mode: deleteMode }),
      });
      const payload = await readApiPayload(response);
      if (!response.ok) throw new Error(payload.error || "ลบลูกค้าไม่สำเร็จ");
      await Promise.all([loadContracts(), loadDashboardStats()]);
    } else {
      if (password !== "14052") throw new Error("รหัสยืนยันการลบไม่ถูกต้อง");
      state.contracts = state.contracts.filter((item) => Number(item.id) !== Number(contract.id));
    }
    delete state.installments[contract.id];
    delete state.prepayments[contract.id];
    state.selectedId = null;
    state.screen = "customers";
    closeModal();
    render();
    showToast(deleteMode === "purge" ? `ลบ ${contract.customer} พร้อมประวัติทั้งหมดแล้ว` : `นำแปลงของ ${contract.customer} ออกและเก็บประวัติไว้แล้ว`);
  } catch (error) {
    throw error;
  } finally {
    if (form.isConnected) setSubmitBusy(form, false);
  }
}

async function skipInstallment(number) {
  const contract = selectedContract();
  const installments = state.installments[contract?.id] || [];
  const installment = installments.find((item) => item.no === Number(number));
  if (!contract || !installment) throw new Error("ไม่พบงวดที่ต้องการอนุญาตข้าม");
  if (["paid", "partial", "waived"].includes(installment.status) || installment.paid > 0) throw new Error("งวดนี้ไม่สามารถข้ามได้");
  if (!isActionableInstallment(installment, installments)) throw new Error("อนุญาตข้ามได้เฉพาะงวดถัดไปตามลำดับเท่านั้น");
  if (isInstallmentLocked(installment.dueDate)) throw new Error("ยังไม่ถึงเดือนที่สามารถอนุญาตข้ามงวดนี้ได้");
  if (!isDemoHost()) {
    const response = await apiRequest("skip-installment", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ installment_id: installment.id }) });
    const payload = await readApiPayload(response);
    if (!response.ok) throw new Error(payload.error || "อนุญาตข้ามงวดไม่สำเร็จ");
    await Promise.all([loadContracts(), loadDashboardStats()]);
    await loadInstallments(contract.id);
  } else {
    const originalAmount = installment.amount;
    const originalPrincipal = installment.principal;
    const originalInterest = installment.interest;
    installment.amount = 0;
    installment.principal = 0;
    installment.interest = 0;
    installment.paid = 0;
    installment.status = "waived";
    const last = installments.at(-1);
    const dueDate = new Date(last.dueDate);
    dueDate.setMonth(dueDate.getMonth() + 1);
    installments.push({ id: Date.now(), no: last.no + 1, dueDate, amount: originalAmount, paid: 0, principal: originalPrincipal, interest: originalInterest, principalPaid: 0, interestPaid: 0, status: "upcoming", slipUrl: "" });
    contract.totalInstallments += 1;
    contract.skippedInstallments = Number(contract.skippedInstallments || 0) + 1;
    contract.expectedAmount = 0;
    contract.status = "waived";
  }
  closeModal(); render(); showToast(`อนุญาตข้ามงวดที่ ${number} และเพิ่มงวดต่อท้ายแล้ว`);
}

async function undoSkipInstallment(number) {
  const contract = selectedContract();
  const installments = state.installments[contract?.id] || [];
  const installment = installments.find((item) => item.no === Number(number));
  if (!contract || installment?.status !== "waived") throw new Error("ไม่พบงวดที่ได้รับอนุญาตให้ข้าม");
  if (!isDemoHost()) {
    const response = await apiRequest("undo-skip", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ installment_id: installment.id }) });
    const payload = await readApiPayload(response);
    if (!response.ok) throw new Error(payload.error || "ยกเลิกการข้ามงวดไม่สำเร็จ");
    await Promise.all([loadContracts(), loadDashboardStats()]);
    await loadInstallments(contract.id);
  } else {
    const appended = installments.at(-1);
    installment.amount = appended.amount;
    installment.principal = appended.principal;
    installment.interest = appended.interest;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const due = new Date(installment.dueDate); due.setHours(0, 0, 0, 0);
    installment.status = due < today ? "overdue" : due.getTime() === today.getTime() ? "due" : "upcoming";
    installments.pop();
    contract.totalInstallments -= 1;
    contract.skippedInstallments = Math.max(0, Number(contract.skippedInstallments || 0) - 1);
    contract.expectedAmount = contract.installmentAmount;
    contract.status = installment.status;
  }
  closeModal(); render(); showToast(`ยกเลิกการข้ามงวดที่ ${number} แล้ว`);
}

async function recordPayment(form) {
  const contract = selectedContract();
  const number = Number(form.dataset.number);
  const installments = state.installments[contract.id] || buildInstallments(contract);
  const installment = installments.find((item) => item.no === number);
  if (!installment || installment.status === "waived" || !isActionableInstallment(installment, installments)) throw new Error("ชำระได้เฉพาะงวดถัดไปตามลำดับเท่านั้น");
  const data = new FormData(form);
  const amount = Number(data.get("amount"));
  const paidDate = new Date(`${String(data.get("paid_date") || "")}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const file = data.get("slip");
  if (Number.isNaN(paidDate.getTime())) throw new Error("ไม่พบวันที่ของงวด กรุณาตรวจสอบตารางผ่อน");
  if (paidDate > today) throw new Error("วันที่รับเงินจริงต้องไม่เกินวันนี้");
  const remainingAmount = Math.max(0, Number(installment.amount) - Number(installment.paid));
  if (Math.abs(amount - remainingAmount) > 0.01) throw new Error(`งวดนี้ต้องชำระเต็มจำนวน ${baht(remainingAmount)} บาท`);
  validateSlipFile(file);
  if (!isDemoHost() && installment?.id) {
    setSubmitBusy(form, true);
    try {
      data.set("installment_id", String(installment.id));
      const response = await apiRequest("payment", { method: "POST", body: data });
      const payload = await readApiPayload(response);
      if (!response.ok) throw new Error(payload.error || "บันทึกการชำระไม่สำเร็จ");
      await Promise.all([loadContracts(), loadInstallments(contract.id), loadDashboardStats()]);
    } finally {
      if (form.isConnected) setSubmitBusy(form, false);
    }
  } else {
    const principalAmount = Math.max(0, Number(installment.principal) - Number(installment.principalPaid));
    const interestAmount = Math.max(0, Number(installment.interest) - Number(installment.interestPaid));
    contract.monthPaid = Number(contract.monthPaid || 0) + amount;
    contract.paidTotal += amount;
    contract.paidPrincipal = Number(contract.paidPrincipal || 0) + principalAmount;
    contract.paidInterest = Number(contract.paidInterest || 0) + interestAmount;
    contract.remainingInterest = Math.max(0, Number(contract.totalInterest || 0) - contract.paidInterest);
    contract.paidInstallments += 1;
    contract.status = "paid";
    installment.paid = installment.amount;
    installment.principalPaid = installment.principal;
    installment.interestPaid = installment.interest;
    installment.paidAt = paidDate;
    installment.status = "paid";
    const monthOffset = (paidDate.getFullYear() - today.getFullYear()) * 12 + paidDate.getMonth() - today.getMonth();
    const incomeIndex = 5 + monthOffset;
    if (incomeIndex >= 0 && incomeIndex < state.monthlyIncome.length) state.monthlyIncome[incomeIndex] = Number(state.monthlyIncome[incomeIndex] || 0) + amount;
    if (incomeIndex >= 0 && incomeIndex < state.monthlyInterest.length) state.monthlyInterest[incomeIndex] = Number(state.monthlyInterest[incomeIndex] || 0) + interestAmount;
    if (incomeIndex === 5) {
      state.interestStats.receivedThisMonth = Number(state.interestStats.receivedThisMonth || 0) + interestAmount;
      state.interestStats.principalThisMonth = Number(state.interestStats.principalThisMonth || 0) + principalAmount;
    }
    state.interestStats.receivedLifetime = Number(state.interestStats.receivedLifetime || 0) + interestAmount;
    state.interestStats.outstanding = Math.max(0, Number(state.interestStats.outstanding || 0) - interestAmount);
  }
  const historical = form.dataset.historical === "true";
  closeModal(); render(); showToast(`${historical ? "บันทึกย้อนหลัง" : "บันทึก"}ยอด ${baht(amount)} บาทและสลิปแล้ว`);
}

async function recordPrincipalPrepayment(form) {
  const contract = selectedContract();
  if (!contract) throw new Error("ไม่พบสัญญาที่ต้องการบันทึกยอดโปะ");
  const installments = state.installments[contract?.id] || buildInstallments(contract);
  const data = new FormData(form);
  const amount = Number(data.get("amount"));
  const remainingPrincipal = Math.max(0, Number(contract.totalPrice) - Number(contract.downPayment) - Number(contract.paidPrincipal ?? contract.paidTotal ?? 0));
  const paidDate = new Date(`${String(data.get("paid_date") || "")}T00:00:00`);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const installment = installmentForPaymentDate(installments, data.get("paid_date"));
  if (!contract || !installment) throw new Error("ไม่พบงวดของเดือนที่ต้องการบันทึกยอดโปะ");
  if (!Number.isFinite(amount) || amount <= 0) throw new Error("กรุณาระบุยอดโปะเงินต้น");
  if (amount > remainingPrincipal + 0.01) throw new Error(`ยอดโปะต้องไม่เกินเงินต้นคงเหลือ ${baht(remainingPrincipal)} บาท`);
  if (Number.isNaN(paidDate.getTime()) || paidDate > today) throw new Error("วันที่รับเงินจริงต้องไม่เกินวันนี้");
  validateSlipFile(data.get("slip"));
  if (isDemoHost()) {
    const monthlyPrincipal = Math.max(0, Number(contract.installmentAmount) - Number(contract.interestAmount || 0));
    const plan = calculatePrepaymentPlan({ remainingPrincipal, extraPrincipal: amount, monthlyPrincipal, monthlyInterest: contract.interestAmount });
    contract.paidPrincipal = Number(contract.paidPrincipal ?? contract.paidTotal ?? 0) + amount;
    contract.paidTotal = Number(contract.paidTotal || 0) + amount;
    contract.totalInstallments = Math.max(contract.paidInstallments, contract.totalInstallments - plan.installmentsReduced);
    state.prepayments[contract.id] ||= [];
    state.prepayments[contract.id].unshift({ id: Date.now(), installmentId: installment.id, installmentNo: installment.no, amount, principal: amount, paidAt: paidDate, slipUrl: "" });
  } else {
    setSubmitBusy(form, true, "กำลังบันทึกยอดโปะ...");
    try {
      data.set("contract_id", String(contract.id));
      const response = await apiRequest("principal-prepayment", { method: "POST", body: data });
      const payload = await readApiPayload(response);
      if (!response.ok) throw new Error(payload.error || "บันทึกยอดโปะเงินต้นไม่สำเร็จ");
      await Promise.all([loadContracts(), loadInstallments(contract.id), loadDashboardStats()]);
    } finally {
      if (form.isConnected) setSubmitBusy(form, false);
    }
  }
  closeModal(); render(); showToast(`บันทึกยอดโปะเงินต้น ฿${baht(amount)} และคำนวณงวดใหม่แล้ว`);
}

async function replacePrepaymentSlip(form) {
  const contract = selectedContract();
  const paymentId = Number(form.dataset.paymentId);
  const payment = (state.prepayments[contract?.id] || []).find((item) => item.id === paymentId);
  if (!payment) throw new Error("ไม่พบรายการโปะที่ต้องการเปลี่ยนสลิป");
  const data = new FormData(form);
  validateSlipFile(data.get("slip"));
  data.set("payment_id", String(paymentId));
  setSubmitBusy(form, true, "กำลังเปลี่ยนสลิปยอดโปะ...");
  try {
    const response = await apiRequest("replace-prepayment-slip", { method: "POST", body: data });
    const payload = await readApiPayload(response);
    if (!response.ok) throw new Error(payload.error || "เปลี่ยนสลิปยอดโปะไม่สำเร็จ");
    await loadInstallments(contract.id);
    closeModal(); render(); showToast("เปลี่ยนสลิปยอดโปะเรียบร้อยแล้ว");
  } finally {
    if (form.isConnected) setSubmitBusy(form, false);
  }
}

async function replaceSlip(form) {
  const contract = selectedContract();
  const number = Number(form.dataset.number);
  const installments = state.installments[contract.id] || [];
  const installment = installments.find((item) => item.no === number);
  if (!installment?.id) throw new Error("ไม่พบงวดที่ต้องการเปลี่ยนสลิป");
  const data = new FormData(form);
  validateSlipFile(data.get("slip"));
  data.set("installment_id", String(installment.id));
  setSubmitBusy(form, true, "กำลังเปลี่ยนสลิป...");
  try {
    const response = await apiRequest("replace-slip", { method: "POST", body: data });
    const payload = await readApiPayload(response);
    if (!response.ok) throw new Error(payload.error || "เปลี่ยนสลิปไม่สำเร็จ");
    await loadInstallments(contract.id);
    closeModal(); render(); showToast(`เปลี่ยนสลิปงวดที่ ${number} เรียบร้อยแล้ว`);
  } finally {
    if (form.isConnected) setSubmitBusy(form, false);
  }
}

async function testSlipStorage(form) {
  const data = new FormData(form);
  validateSlipFile(data.get("slip"));
  setSubmitBusy(form, true, "กำลังทดสอบ...");
  try {
    const response = await apiRequest("upload-check", { method: "POST", body: data });
    const payload = await readApiPayload(response);
    if (!response.ok) throw new Error(payload.error || "ทดสอบการแนบสลิปไม่สำเร็จ");
    closeModal(); showToast(payload.message || "ระบบแนบสลิปทำงานปกติ");
  } finally {
    if (form.isConnected) setSubmitBusy(form, false);
  }
}

async function installApp() {
  if (state.installPrompt) {
    await state.installPrompt.prompt();
    const choice = await state.installPrompt.userChoice;
    if (choice.outcome === "accepted") state.showInstall = false;
    state.installPrompt = null;
    render();
    return;
  }
  showToast(/iphone|ipad|ipod/i.test(navigator.userAgent) ? "บน iPhone: กดปุ่มแชร์ แล้วเลือก “เพิ่มไปยังหน้าจอโฮม”" : "เปิดเมนูเบราว์เซอร์ แล้วเลือก “ติดตั้งแอป”");
}

window.addEventListener("beforeinstallprompt", (event) => {
  event.preventDefault(); state.installPrompt = event; state.showInstall = true; render();
});

let deviceResizeTimer;
window.addEventListener("resize", () => {
  window.clearTimeout(deviceResizeTimer);
  deviceResizeTimer = window.setTimeout(() => {
    const nextLayout = detectDeviceLayout();
    document.documentElement.dataset.device = nextLayout;
    if (nextLayout !== deviceLayout) {
      deviceLayout = nextLayout;
      render();
    }
  }, 120);
});

if ("serviceWorker" in navigator) {
  if (isDemoHost()) {
    navigator.serviceWorker.getRegistrations().then((registrations) => Promise.all(registrations.map((registration) => registration.unregister())));
    if ("caches" in window) caches.keys().then((keys) => Promise.all(keys.filter((key) => key.startsWith("saithong-")).map((key) => caches.delete(key))));
  } else {
    navigator.serviceWorker.register("/sw.js");
  }
}

async function bootstrap() {
  if (!state.authenticated) {
    render();
    return;
  }
  try {
    await loadSession();
    if (state.activeWorkspaceId && !isDemoHost()) await Promise.all([loadContracts(), loadDashboardStats()]);
    render();
    if (demoPreviewScreen === "payment" && selectedContract()) {
      setTimeout(() => {
        const contract = selectedContract();
        const installment = (state.installments[contract.id] || buildInstallments(contract)).find((item) => !["paid", "waived"].includes(item.status));
        if (installment) showPaymentModal(installment.no);
      }, 0);
    }
  } catch (error) {
    state.contracts = [];
    render();
    showToast(error.message || "ยังเชื่อมต่อ MySQL ไม่ได้ กรุณาตรวจการตั้งค่า Hosting");
  }
}

bootstrap();
